import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import YouTube from 'react-youtube';
import AdSense from './components/AdSense';
import EmailModal from './EmailModal';

// Base URL configuration for API calls
const BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://livingsneaky.com'
  : 'http://localhost:5000';

// OpenAI API Key from environment variables
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY || process.env.OPENAI_API_KEY || '';

// YouTube API Key from environment variables  
const YOUTUBE_API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY || process.env.YOUTUBE_API_KEY || 'AIzaSyDqweEKiKN7melgdenoZp725LYge3XtbPo';

function App() {
  const [prompt, setPrompt] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('generate');
  const [editFile, setEditFile] = useState(null);
  const [title, setTitle] = useState("");
  const [timedLyrics, setTimedLyrics] = useState([]);
  const [videoId, setVideoId] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const playerRef = useRef(null);
  const activeLineRef = useRef(null);
  const [userRegistered, setUserRegistered] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Add registration loading state
  const [registrationLoading, setRegistrationLoading] = useState(false);

  // Email collection state
  const [userEmail, setUserEmail] = useState(localStorage.getItem('userEmail') || '');
  const [showEmailModal, setShowEmailModal] = useState(!localStorage.getItem('userEmail'));
  const [emailInput, setEmailInput] = useState('');
  const [authError, setAuthError] = useState('');

  // Add floating player state
  const [floatingPlayer, setFloatingPlayer] = useState({
    isVisible: false,
    videoId: '',
    title: '',
    timedLyrics: [],
    currentTime: 0,
    isMinimized: false,
    isPlaying: false,
    userPaused: false
  });

  // Add search loading state
  const [searchLoading, setSearchLoading] = useState(false);

  // Add state for managing expanded descriptions
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  // Add loading state for images
  const [imagesLoading, setImagesLoading] = useState(true);

  // Add state for total images count
  const [totalImages, setTotalImages] = useState(0);

  // Add state for Work tab
  const [workProjects, setWorkProjects] = useState([
    {
      id: 1,
      title: "LivingSneaky Studio",
      description: "Full-stack AI-powered creative suite with image generation, karaoke, and portfolio features. Built with React, Node.js, and OpenAI integration.",
      status: "Completed",
      tech: ["React", "Node.js", "OpenAI", "MongoDB", "Express", "YouTube API"],
      progress: 100,
      image: "https://via.placeholder.com/400x250/ff6b35/ffffff?text=LivingSneaky+Studio",
      demoUrl: "https://livingsneaky.com",
      githubUrl: "https://github.com/ryan-developer/livingsneaky-studio",
      features: ["AI Image Generation", "Karaoke System", "Portfolio Management", "User Authentication"],
      startDate: "2024-01",
      endDate: "2024-03"
    },
    {
      id: 2,
      title: "AI Chat Assistant",
      description: "Intelligent conversational AI with memory, context awareness, and multi-modal capabilities. Supports text, voice, and image interactions.",
      status: "In Progress",
      tech: ["Python", "FastAPI", "OpenAI", "WebSocket", "React", "TensorFlow"],
      progress: 75,
      image: "https://via.placeholder.com/400x250/667eea/ffffff?text=AI+Chat+Assistant",
      demoUrl: "https://ai-chat-demo.com",
      githubUrl: "https://github.com/ryan-developer/ai-chat-assistant",
      features: ["Natural Language Processing", "Voice Recognition", "Image Analysis", "Memory System"],
      startDate: "2024-02",
      endDate: "2024-04"
    },
    {
      id: 3,
      title: "E-Commerce Platform",
      description: "Modern e-commerce solution with real-time inventory, payment processing, and advanced analytics dashboard.",
      status: "Completed",
      tech: ["Next.js", "TypeScript", "Stripe", "PostgreSQL", "Redis", "Docker"],
      progress: 100,
      image: "https://via.placeholder.com/400x250/11998e/ffffff?text=E-Commerce+Platform",
      demoUrl: "https://ecommerce-demo.com",
      githubUrl: "https://github.com/ryan-developer/ecommerce-platform",
      features: ["Payment Processing", "Inventory Management", "Analytics Dashboard", "Mobile Responsive"],
      startDate: "2023-10",
      endDate: "2024-01"
    },
    {
      id: 4,
      title: "Real-Time Collaboration Tool",
      description: "Team collaboration platform with live document editing, video calls, and project management features.",
      status: "In Progress",
      tech: ["Vue.js", "Socket.io", "WebRTC", "MongoDB", "AWS", "Docker"],
      progress: 60,
      image: "https://via.placeholder.com/400x250/9c27b0/ffffff?text=Collaboration+Tool",
      demoUrl: "https://collab-demo.com",
      githubUrl: "https://github.com/ryan-developer/collaboration-tool",
      features: ["Live Document Editing", "Video Conferencing", "Project Management", "File Sharing"],
      startDate: "2024-03",
      endDate: "2024-06"
    },
    {
      id: 5,
      title: "IoT Dashboard",
      description: "Comprehensive IoT device management system with real-time monitoring, alerts, and data visualization.",
      status: "Completed",
      tech: ["React", "D3.js", "MQTT", "InfluxDB", "Grafana", "Raspberry Pi"],
      progress: 100,
      image: "https://via.placeholder.com/400x250/ff9800/ffffff?text=IoT+Dashboard",
      demoUrl: "https://iot-dashboard-demo.com",
      githubUrl: "https://github.com/ryan-developer/iot-dashboard",
      features: ["Real-time Monitoring", "Data Visualization", "Alert System", "Device Management"],
      startDate: "2023-08",
      endDate: "2023-12"
    },
    {
      id: 6,
      title: "Machine Learning Pipeline",
      description: "Automated ML pipeline for data processing, model training, and deployment with MLOps best practices.",
      status: "Completed",
      tech: ["Python", "TensorFlow", "Kubernetes", "MLflow", "Apache Airflow", "AWS"],
      progress: 100,
      image: "https://via.placeholder.com/400x250/4caf50/ffffff?text=ML+Pipeline",
      demoUrl: "https://ml-pipeline-demo.com",
      githubUrl: "https://github.com/ryan-developer/ml-pipeline",
      features: ["Automated Training", "Model Versioning", "A/B Testing", "Performance Monitoring"],
      startDate: "2023-06",
      endDate: "2023-10"
    }
  ]);

  // Add state for Find GF tab
  const [datingProfile, setDatingProfile] = useState({
    name: "Ryan",
    age: 25,
    bio: "Full-Stack Developer & AI Enthusiast. I build amazing web applications and love exploring new technologies. Looking for someone who shares my passion for innovation and creativity!",
    interests: ["Coding", "AI/ML", "Music", "Gaming", "Travel", "Photography"],
    photos: [
      "https://via.placeholder.com/300x400/ff6b35/ffffff?text=Ryan+Photo+1",
      "https://via.placeholder.com/300x400/f7931e/ffffff?text=Ryan+Photo+2",
      "https://via.placeholder.com/300x400/ff6b35/ffffff?text=Ryan+Photo+3"
    ]
  });

  // Add state for AI girlfriend functionality
  const [girlfriend, setGirlfriend] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [girlfriendLoading, setGirlfriendLoading] = useState(false);
  const [preferences, setPreferences] = useState('');

  // Add state for shorts functionality
  const [shortVideoPrompt, setShortVideoPrompt] = useState('');
  const [shortVideoLoading, setShortVideoLoading] = useState(false);
  const [shortVideoDuration, setShortVideoDuration] = useState(5);
  const [shortVideoStyle, setShortVideoStyle] = useState('trendy');
  const [shortVideoPlatform, setShortVideoPlatform] = useState('tiktok');
  const [generatedShortVideo, setGeneratedShortVideo] = useState(null);
  const [userShortVideos, setUserShortVideos] = useState([]);

  // Add new state variable for language selection
  const [shortVideoLanguage, setShortVideoLanguage] = useState('english');

  const [matches, setMatches] = useState([
    {
      id: 1,
      name: "Sarah",
      age: 23,
      bio: "UX Designer who loves creating beautiful interfaces",
      photo: "https://via.placeholder.com/200x250/e91e63/ffffff?text=Sarah",
      compatibility: 92
    },
    {
      id: 2,
      name: "Emma",
      age: 26,
      bio: "Software Engineer passionate about clean code",
      photo: "https://via.placeholder.com/200x250/9c27b0/ffffff?text=Emma",
      compatibility: 88
    },
    {
      id: 3,
      name: "Jessica",
      age: 24,
      bio: "Digital Artist and Tech Enthusiast",
      photo: "https://via.placeholder.com/200x250/3f51b5/ffffff?text=Jessica",
      compatibility: 85
    }
  ]);

  // Add state for project management
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    status: 'In Progress',
    tech: [],
    progress: 0,
    image: '',
    demoUrl: '',
    githubUrl: '',
    features: [],
    startDate: '',
    endDate: ''
  });

  // Function to add new project
  const addNewProject = () => {
    if (newProject.title && newProject.description) {
      const project = {
        ...newProject,
        id: workProjects.length + 1,
        tech: newProject.tech.filter(t => t.trim() !== ''),
        features: newProject.features.filter(f => f.trim() !== '')
      };
      setWorkProjects([...workProjects, project]);
      setNewProject({
        title: '',
        description: '',
        status: 'In Progress',
        tech: [],
        progress: 0,
        image: '',
        demoUrl: '',
        githubUrl: '',
        features: [],
        startDate: '',
        endDate: ''
      });
      setShowAddProjectModal(false);
      alert('✅ Project added successfully!');
    } else {
      alert('❌ Please fill in at least the title and description.');
    }
  };

  // Register user function
  const registerUser = useCallback(async (email) => {
    if (!email || !email.trim()) {
      setAuthError('Please enter a valid email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setAuthError('Please enter a valid email address');
      return;
    }

    setRegistrationLoading(true);
    setAuthError('');
    
    try {
      const response = await axios.post(`${BASE_URL}/register-user`, {
        email: email.toLowerCase().trim()
      });
      
      if (response.data.success) {
        const normalizedEmail = email.toLowerCase().trim();
        setUserEmail(normalizedEmail);
        localStorage.setItem('userEmail', normalizedEmail);
        setUserRegistered(true);
        setShowEmailModal(false);
        setEmailInput('');
        console.log('User registered successfully:', response.data.message);
      } else {
        setAuthError(response.data.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setAuthError(error.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setRegistrationLoading(false);
    }
  }, []);

  // Check user registration status on component mount
  useEffect(() => {
    const checkUserStatus = async () => {
      // If no email is stored, show the modal
      if (!userEmail) {
        setShowEmailModal(true);
        return;
      }

      try {
        const response = await axios.get(`${BASE_URL}/check-user`, {
          headers: {
            'x-user-email': userEmail
          }
        });
        
        if (response.data.isRegistered) {
          setUserRegistered(true);
        } else {
          // Auto-register the user with stored email
          await registerUser(userEmail);
        }
      } catch (error) {
        console.error('Error checking user status:', error);
        // Try to register anyway
        await registerUser(userEmail);
      }
    };

    checkUserStatus();
  }, [registerUser, userEmail]);

  // Add useEffect to clear preview when switching tabs
  useEffect(() => {
    // Clear image preview when switching away from edit tab
    if (tab !== 'edit') {
      setImagePreview(null);
      setEditFile(null);
    }
  }, [tab]);

  // Add useEffect to fetch short videos when shorts tab is selected
  useEffect(() => {
    if (tab === 'shorts' && userRegistered) {
      fetchUserShortVideos();
    }
  }, [tab, userRegistered]);

  // Function to toggle description expansion
  const toggleDescription = (index) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Function to truncate text
  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  console.log('🚀 App component rendering, images state:', images);
  console.log('🚀 Images length:', images?.length);
  console.log('🚀 Current tab:', tab);

  // Add responsive CSS styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .image-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 1rem;
      }
      
      @media (max-width: 1200px) {
        .image-grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }
      
      @media (max-width: 900px) {
        .image-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      
      @media (max-width: 600px) {
        .image-grid {
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }
      }
      
      .image-card {
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        transform: translateY(0);
        opacity: 1;
        animation: fadeInUp 0.6s ease-out;
        background: linear-gradient(145deg, #2a2a2a, #1e1e1e);
        border: 1px solid transparent;
        background-clip: padding-box;
      }
      
      .image-card:hover {
        transform: translateY(-8px) scale(1.02);
        box-shadow: 0 20px 40px rgba(255, 107, 53, 0.3), 0 0 0 1px rgba(255, 107, 53, 0.5);
        border-color: #ff6b35 !important;
        background: linear-gradient(145deg, #2f2f2f, #232323);
      }
      
      .image-card img {
        transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      .image-card:hover img {
        transform: scale(1.08);
      }
      
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateX(-20px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      
      @keyframes pulse {
        0%, 100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.05);
        }
      }
      
      .loading-skeleton {
        background: linear-gradient(90deg, #2a2a2a 25%, #3a3a3a 50%, #2a2a2a 75%);
        background-size: 200% 100%;
        animation: loading 1.5s infinite;
      }
      
      @keyframes loading {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
      
      @keyframes floatUp {
        from {
          opacity: 0;
          transform: translateY(100px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .spinner {
        width: 16px;
        height: 16px;
        border: 2px solid #333;
        border-top: 2px solid #ff6b35;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
      
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      .nav-button {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.1);
      }
      
      .nav-button::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transition: left 0.5s;
      }
      
      .nav-button:hover::before {
        left: 100%;
      }
      
      .nav-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(255, 107, 53, 0.3);
        background: linear-gradient(135deg, rgba(255, 107, 53, 0.2), rgba(247, 147, 30, 0.2));
      }
      
      .floating-player {
        animation: slideInFromRight 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        backdrop-filter: blur(20px);
        border: 1px solid rgba(255, 255, 255, 0.1);
        background: linear-gradient(145deg, rgba(0, 0, 0, 0.9), rgba(20, 20, 20, 0.9));
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 107, 53, 0.2);
      }
      
      @keyframes slideInFromRight {
        from {
          opacity: 0;
          transform: translateX(100%);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      
      .content-section {
        animation: slideIn 0.6s ease-out;
      }
      
      .generate-button {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
        background: linear-gradient(135deg, #ff6b35, #f7931e, #ff6b35);
        background-size: 200% 200%;
        animation: gradientShift 3s ease infinite;
      }
      
      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      
      .generate-button:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 12px 24px rgba(255, 107, 53, 0.4);
        background: linear-gradient(135deg, #ff8a5c, #ffb347, #ff8a5c);
      }
      
      .generate-button:active:not(:disabled) {
        transform: translateY(0);
      }
      
      .sidebar-card {
        transition: all 0.3s ease;
        animation: slideIn 0.6s ease-out;
        background: linear-gradient(145deg, #2a2a2a, #1f1f1f);
        border: 1px solid rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
      }
      
      .sidebar-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
        background: linear-gradient(145deg, #2f2f2f, #242424);
        border-color: rgba(255, 107, 53, 0.3);
      }
      
      /* Responsive AdSense */
      @media (max-width: 1200px) {
        .sidebar-ad {
          display: none !important;
        }
        .main-content-responsive {
          margin: 0 !important;
          max-width: 100% !important;
        }
      }
      
      @media (max-width: 768px) {
        .adsense-banner {
          padding: 1rem 0 !important;
        }
        .adsense-banner .adsense-container {
          height: 150px !important;
        }
      }

      /* Girlfriend Chat Responsive Styles */
      .girlfriend-chat-container {
        display: grid;
        grid-template-columns: 300px 1fr;
        gap: 1.5rem;
        height: 600px;
      }
      
      @media (max-width: 1024px) {
        .girlfriend-chat-container {
          grid-template-columns: 250px 1fr;
          gap: 1rem;
          height: 500px;
        }
      }
      
      @media (max-width: 768px) {
        .girlfriend-chat-container {
          grid-template-columns: 1fr;
          height: auto;
          gap: 1rem;
        }
        
        .girlfriend-profile-sidebar {
          order: 2;
          height: auto !important;
        }
        
        .girlfriend-chat-area {
          order: 1;
          height: 400px !important;
        }
      }
      
      @media (max-width: 480px) {
        .girlfriend-chat-container {
          gap: 0.75rem;
        }
        
        .girlfriend-chat-area {
          height: 350px !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Fetch images from the backend
  const fetchImages = useCallback(async () => {
    console.log('fetchImages called');
    setImagesLoading(true);
    try {
      console.log('Fetching images from:', `${BASE_URL}/images`);
      const response = await axios.get(`${BASE_URL}/images`);
      console.log('Images response:', response.data);
      
      // Handle new response format with images and total
      if (response.data && response.data.images && Array.isArray(response.data.images)) {
        setImages(response.data.images);
        setTotalImages(response.data.total || response.data.images.length);
        console.log('Images set from API:', response.data.images);
        console.log('Total count from API:', response.data.total);
      } else if (Array.isArray(response.data)) {
        // Fallback for old format (just array)
        setImages(response.data);
        setTotalImages(response.data.length);
        console.log('Images set from API (old format):', response.data);
      } else {
        console.warn('API response format not recognized:', response.data);
        setImages([]);
        setTotalImages(0);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      console.log('Setting dummy data...');
      // Set some dummy data for testing
      const dummyImages = [
        {
          url: 'https://via.placeholder.com/300x200/ff6b35/ffffff?text=Test+Image+1',
          prompt: 'A beautiful sunset over mountains with vibrant colors',
          type: 'GENERATED',
          createdAt: new Date().toISOString()
        },
        {
          url: 'https://via.placeholder.com/300x200/f7931e/ffffff?text=Test+Image+2',
          prompt: 'A serene lake surrounded by autumn trees',
          type: 'EDITED',
          createdAt: new Date().toISOString()
        },
        {
          url: 'https://via.placeholder.com/300x200/4CAF50/ffffff?text=Test+Image+3',
          prompt: 'A futuristic city skyline at night',
          type: 'GENERATED',
          createdAt: new Date().toISOString()
        }
      ];
      setImages(dummyImages);
      setTotalImages(dummyImages.length);
      console.log('Dummy images set:', dummyImages);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // Add debugging for images state
  useEffect(() => {
    console.log('Images state changed:', images);
    console.log('Images is array:', Array.isArray(images));
    console.log('Images length:', images?.length);
  }, [images]);

  // Generate image
  const generateImage = async () => {
    if (!prompt.trim()) return;
    
    if (!userRegistered) {
      alert('Please wait while we set up your account...');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/generate-image`, { prompt }, {
        headers: {
          'x-user-email': userEmail
        }
      });
      if (response.data && (response.data.imageUrl || response.data.fileUrl)) {
        await fetchImages(); // Refresh the images list
        setPrompt('');
      } else {
        console.error('No image returned.');
      }
    } catch (error) {
      console.error('Error generating image:', error);
      // Show user-friendly error message
      alert('Error generating image: ' + (error.response?.data?.message || error.message || 'Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Edit image
  const editImage = async () => {
    if (!editFile || !prompt.trim()) return;
    
    if (!userRegistered) {
      alert('Please wait while we set up your account...');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', editFile);
      formData.append('prompt', prompt);

      const response = await axios.post(`${BASE_URL}/edit-image`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'x-user-email': userEmail
        }
      });

      if (response.data && response.data.fileUrl) {
        await fetchImages();
        setPrompt('');
        setEditFile(null);
        setImagePreview(null); // Clear the preview
        
        // Navigate to Generate tab to show the results
        setTab('generate');
        
        // Show success message
        alert('Image edited successfully! Check out your edited image in the gallery.');
      } else {
        console.error('No image returned.');
        alert('Image editing completed but no result returned. Please check the gallery.');
      }
    } catch (error) {
      console.error('Error editing image:', error);
      // Show user-friendly error message
      alert('Error editing image: ' + (error.response?.data?.message || error.message || 'Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch lyrics and timestamps
  const fetchLyricsAndTimestamps = async (input) => {
    if (!input.trim()) {
      alert('Please enter a song title or artist.');
      return;
    }

    setSearchLoading(true);
    try {
      console.log('🎵 Searching for:', input);
      const response = await axios.post(`${BASE_URL}/lyrics`, { input });
      
      console.log('🎵 Karaoke API Response:', response.data);
      
      if (response.data.success) {
        setTitle(response.data.title);
        setTimedLyrics(response.data.timedLyrics);
        setVideoId(response.data.videoId);
        
        // Set up floating player with enhanced data
        setFloatingPlayer({
          isVisible: true,
          videoId: response.data.videoId,
          title: response.data.title,
          timedLyrics: response.data.timedLyrics,
          currentTime: 0,
          isMinimized: false,
          isPlaying: false,
          userPaused: false
        });

        // Show success message with details
        const successMessage = response.data.fallback 
          ? `🎭 ${response.data.message}\n\n🎵 Enjoy the karaoke experience!`
          : `🎉 Found: "${response.data.title}"\n${response.data.channelTitle ? `📺 Channel: ${response.data.channelTitle}` : ''}\n\n🎵 Ready to sing along!`;
        
        alert(successMessage);
        
        console.log('✅ Karaoke setup complete:', {
          title: response.data.title,
          videoId: response.data.videoId,
          lyricsCount: response.data.timedLyrics?.length,
          fallback: response.data.fallback
        });
      } else {
        // Handle API success: false
        console.warn('⚠️ Karaoke API returned success: false:', response.data);
        alert(`🎵 ${response.data.message || 'Could not find the song. Please try a different search term.'}\n\n💡 Try searching with just the song title or artist name.`);
      }
    } catch (error) {
      console.error('💥 Error fetching lyrics:', error);
      
      // Provide detailed error information
      let errorMessage = '🎵 Karaoke Error\n\n';
      
      if (error.response) {
        // Server responded with error status
        console.error('📡 Server Error Response:', error.response.data);
        errorMessage += `Server Error (${error.response.status}): ${error.response.data?.message || error.response.data?.error || 'Unknown server error'}`;
        
        if (error.response.status === 403) {
          errorMessage += '\n\n💡 This might be a YouTube API quota issue. Please try again later.';
        } else if (error.response.status === 400) {
          errorMessage += '\n\n💡 Please check your search term and try again.';
        }
      } else if (error.request) {
        // Network error
        console.error('🌐 Network Error:', error.request);
        errorMessage += 'Network connection error. Please check your internet connection and try again.';
      } else {
        // Other error
        console.error('❓ Unknown Error:', error.message);
        errorMessage += `Unexpected error: ${error.message}`;
      }
      
      errorMessage += '\n\n🔄 Please try again with a different search term.';
      alert(errorMessage);
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle search
  const handleSearch = async () => {
    const input = document.getElementById('searchInput').value;
    await fetchLyricsAndTimestamps(input);
  };

  // Close floating player
  const closeFloatingPlayer = () => {
    setFloatingPlayer(prev => ({
      ...prev,
      isVisible: false,
      isPlaying: false,
      userPaused: false
    }));
    
    if (playerRef.current && playerRef.current.getInternalPlayer) {
      try {
        const player = playerRef.current.getInternalPlayer();
        if (player && typeof player.pauseVideo === 'function') {
          player.pauseVideo();
        }
      } catch (error) {
        console.error('Error pausing player:', error);
      }
    }
  };

  // Toggle floating player minimize
  const toggleFloatingPlayerMinimize = () => {
    setFloatingPlayer(prev => ({
      ...prev,
      isMinimized: !prev.isMinimized
    }));
  };

  // Get current lyric index for floating player
  const getFloatingCurrentIndex = () => {
    for (let i = 0; i < floatingPlayer.timedLyrics.length; i++) {
      if (floatingPlayer.currentTime < floatingPlayer.timedLyrics[i].time) {
        return i - 1;
      }
    }
    return floatingPlayer.timedLyrics.length - 1;
  };

  // YouTube player event handlers with safety checks
  const onPlayerReady = (event) => {
    try {
      playerRef.current = event.target;
      
      // Set up time update interval with safety checks
      const interval = setInterval(() => {
        try {
          if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
            const time = playerRef.current.getCurrentTime();
            setCurrentTime(time);
            setFloatingPlayer(prev => ({ ...prev, currentTime: time }));
          }
        } catch (error) {
          console.error('Error getting current time:', error);
        }
      }, 100);

      // Clean up interval when component unmounts
      return () => clearInterval(interval);
    } catch (error) {
      console.error('Error in onPlayerReady:', error);
    }
  };

  const onPlayerError = (event) => {
    console.error('YouTube Player Error:', event.data);
    setFloatingPlayer(prev => ({ ...prev, isPlaying: false }));
  };

  const onPlayerStateChange = (event) => {
    try {
      if (event.data === 1) { // Playing
        setFloatingPlayer(prev => ({ ...prev, isPlaying: true }));
      } else if (event.data === 2) { // Paused
        setFloatingPlayer(prev => ({ ...prev, isPlaying: false }));
      }
    } catch (error) {
      console.error('Error in onPlayerStateChange:', error);
    }
  };

  // Safe player controls
  const togglePlayPause = () => {
    try {
      if (playerRef.current && playerRef.current.getInternalPlayer) {
        const player = playerRef.current.getInternalPlayer();
        if (player && typeof player.getPlayerState === 'function') {
          const state = player.getPlayerState();
          if (state === 1) { // Playing
            if (typeof player.pauseVideo === 'function') {
              player.pauseVideo();
              setFloatingPlayer(prev => ({ ...prev, userPaused: true }));
            }
          } else { // Paused or other
            if (typeof player.playVideo === 'function') {
              player.playVideo();
              setFloatingPlayer(prev => ({ ...prev, userPaused: false }));
            }
          }
        }
      }
    } catch (error) {
      console.error('Error toggling play/pause:', error);
    }
  };

  // Helper function to render favorites safely
  const renderFavorites = (favorites) => {
    if (!favorites) return null;
    
    if (typeof favorites === 'string') {
      return favorites;
    }
    
    if (typeof favorites === 'object') {
      return Object.entries(favorites)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
    }
    
    return 'Various wonderful things';
  };

  // Generate AI Girlfriend
  const generateGirlfriend = async () => {
    if (!userRegistered) {
      alert('Please wait while we set up your account...');
      return;
    }

    setGirlfriendLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/generate-girlfriend`, {
        preferences: preferences.trim() || undefined
      }, {
        headers: {
          'x-user-email': userEmail
        }
      });

      if (response.data.success) {
        const newGirlfriend = response.data.girlfriend;
        setGirlfriend(newGirlfriend);
        
        // Add initial greeting message
        const greetingMessage = {
          sender: 'girlfriend',
          message: `Hi there! I'm ${newGirlfriend.name}! 💕 I'm so excited to meet you! How are you doing today?`,
          timestamp: new Date().toLocaleTimeString()
        };
        setChatMessages([greetingMessage]);
        
        console.log('✅ Generated girlfriend:', newGirlfriend);
      }
    } catch (error) {
      console.error('Error generating girlfriend:', error);
      alert('Error generating girlfriend: ' + (error.response?.data?.error || error.message || 'Please try again.'));
    } finally {
      setGirlfriendLoading(false);
    }
  };

  // Send chat message
  const sendChatMessage = async () => {
    if (!chatInput.trim() || !girlfriend || chatLoading) return;

    const userMessage = {
      sender: 'user',
      message: chatInput.trim(),
      timestamp: new Date().toLocaleTimeString()
    };

    // Add user message immediately
    setChatMessages(prev => [...prev, userMessage]);
    const currentInput = chatInput.trim();
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await axios.post(`${BASE_URL}/chat-girlfriend`, {
        message: currentInput,
        girlfriendData: girlfriend,
        chatHistory: chatMessages
      }, {
        headers: {
          'x-user-email': userEmail
        }
      });

      if (response.data.success) {
        const girlfriendMessage = {
          sender: 'girlfriend',
          message: response.data.message,
          timestamp: new Date().toLocaleTimeString()
        };
        setChatMessages(prev => [...prev, girlfriendMessage]);
      }
    } catch (error) {
      console.error('Error sending chat message:', error);
      // Add error message to chat
      const errorMessage = {
        sender: 'system',
        message: 'Sorry, I had trouble responding. Please try again.',
        timestamp: new Date().toLocaleTimeString()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setChatLoading(false);
    }
  };

  // Handle Enter key in chat input
  const handleChatKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  // Generate short video function
  const generateShortVideo = async () => {
    if (!shortVideoPrompt.trim()) {
      alert('Please enter a prompt for your short video');
      return;
    }

    setShortVideoLoading(true);
    setGeneratedShortVideo(null);

    try {
      console.log('🎬 Generating short video with:', {
        prompt: shortVideoPrompt,
        duration: shortVideoDuration,
        style: shortVideoStyle,
        platform: shortVideoPlatform,
        language: shortVideoLanguage // Add language parameter
      });

      console.log('📡 Making request to:', `${BASE_URL}/generate-short-video`);
      console.log('📧 User email:', userEmail);

      const response = await fetch(`${BASE_URL}/generate-short-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-email': userEmail
        },
        body: JSON.stringify({
          prompt: shortVideoPrompt,
          duration: shortVideoDuration,
          style: shortVideoStyle,
          platform: shortVideoPlatform,
          language: shortVideoLanguage // Add language parameter
        })
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('❌ Non-JSON response received:', textResponse.substring(0, 500));
        throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📡 Response data:', data);

      if (data.success) {
        console.log('✅ Short video generated successfully:', data.videoData);
        setGeneratedShortVideo(data.videoData);
        
        // Add to user videos list
        setUserShortVideos(prev => [data.videoData, ...prev]);
        
        // Clear the prompt
        setShortVideoPrompt('');
        
        alert(`${data.message}`);
      } else {
        throw new Error(data.error || 'Failed to generate short video');
      }
    } catch (error) {
      console.error('❌ Short video generation error:', error);
      
      // More specific error messages
      if (error.message.includes('Failed to fetch')) {
        alert('Failed to connect to server. Please make sure the backend is running on port 5000.');
      } else if (error.message.includes('HTML instead of JSON')) {
        alert('Server error: The backend returned an error page instead of a proper response. Please check if the user is registered and the database is connected.');
      } else {
        alert('Failed to generate short video: ' + error.message);
      }
    } finally {
      setShortVideoLoading(false);
    }
  };

  // Fetch user's short videos
  const fetchUserShortVideos = async () => {
    try {
      const response = await fetch(`${BASE_URL}/user-videos?limit=20`, {
        headers: {
          'x-user-email': userEmail
        }
      });

      const data = await response.json();
      if (data.success) {
        // Filter for short videos only
        const shortVideos = data.videos.filter(video => 
          video.duration <= 30 && (video.metadata?.isShortForm || video.fileName.includes('short_'))
        );
        setUserShortVideos(shortVideos);
      }
    } catch (error) {
      console.error('Error fetching user short videos:', error);
    }
  };

  // Add state for video modal
  const [videoModal, setVideoModal] = useState({
    isOpen: false,
    videoUrl: '',
    title: ''
  });

  // Add function to handle video viewing
  const openVideoModal = (videoUrl, title) => {
    console.log('Opening video:', videoUrl, title);
    setVideoModal({
      isOpen: true,
      videoUrl: `${BASE_URL}${videoUrl}`,
      title: title
    });
  };

  const closeVideoModal = () => {
    setVideoModal({ show: false, url: '', title: '' });
  };

  // Add feedback state variables
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    type: 'general',
    subject: '',
    message: '',
    priority: 'medium'
  });
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [showMyFeedback, setShowMyFeedback] = useState(false);
  const [userFeedback, setUserFeedback] = useState([]);
  const [feedbackPage, setFeedbackPage] = useState(1);

  // Feedback functions
  const openFeedbackModal = () => {
    setShowFeedbackModal(true);
    setFeedbackSuccess(false);
  };

  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
    setFeedbackForm({
      type: 'general',
      subject: '',
      message: '',
      priority: 'medium'
    });
    setFeedbackSuccess(false);
  };

  const submitFeedback = async () => {
    if (!feedbackForm.subject.trim() || !feedbackForm.message.trim()) {
      alert('Please fill in both subject and message fields.');
      return;
    }

    if (!userEmail) {
      alert('Please register first to submit feedback.');
      return;
    }

    setFeedbackLoading(true);
    try {
      const response = await fetch('/submit-feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: userEmail,
          type: feedbackForm.type,
          subject: feedbackForm.subject,
          message: feedbackForm.message,
          priority: feedbackForm.priority,
          url: window.location.href
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setFeedbackSuccess(true);
        setFeedbackForm({
          type: 'general',
          subject: '',
          message: '',
          priority: 'medium'
        });
        setTimeout(() => {
          closeFeedbackModal();
        }, 2000);
      } else {
        alert(data.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const fetchMyFeedback = async () => {
    if (!userEmail) return;

    try {
      const response = await fetch(`/my-feedback?page=${feedbackPage}&limit=10`, {
        headers: {
          'x-user-email': userEmail
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setUserFeedback(data.feedback);
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
    }
  };

  const toggleMyFeedback = () => {
    setShowMyFeedback(!showMyFeedback);
    if (!showMyFeedback) {
      fetchMyFeedback();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 50%, #1a1a1a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 50%, #2a2a2a 100%)',
        borderBottom: '1px solid #333',
        padding: '0.75rem 0',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backdropFilter: 'blur(10px)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              animation: 'pulse 2s infinite'
            }}>
              LS
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#ffffff'
              }}>
                LivingSneaky
              </h1>
              <p style={{
                margin: 0,
                fontSize: '0.8rem',
                color: '#888',
                fontWeight: '400'
              }}>
                Studio
              </p>
              <p style={{
                margin: 0,
                fontSize: '0.7rem',
                color: '#666'
              }}>
                AI-Powered Creative Suite
              </p>
            </div>
          </div>

          {/* Navigation */}
          <nav style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setTab('generate')}
              className="nav-button"
              style={{
                padding: '0.75rem 1.5rem',
                background: tab === 'generate' ? 'linear-gradient(135deg, #ff6b35, #f7931e)' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              🌟 Generate
            </button>
            <button
              onClick={() => setTab('edit')}
              className="nav-button"
              style={{
                padding: '0.75rem 1.5rem',
                background: tab === 'edit' ? 'linear-gradient(135deg, #ff6b35, #f7931e)' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              ✏️ Edit
            </button>
            <button
              onClick={() => setTab('karaoke')}
              className="nav-button"
              style={{
                padding: '0.75rem 1.5rem',
                background: tab === 'karaoke' ? 'linear-gradient(135deg, #ff6b35, #f7931e)' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              🎵 Karaoke
            </button>
            <button
              onClick={() => setTab('shorts')}
              className="nav-button"
              style={{
                padding: '0.75rem 1.5rem',
                background: tab === 'shorts' ? 'linear-gradient(135deg, #ff6b35, #f7931e)' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              📱 Shorts
            </button>
            <button
              onClick={() => setTab('work')}
              className="nav-button"
              style={{
                padding: '0.75rem 1.5rem',
                background: tab === 'work' ? 'linear-gradient(135deg, #ff6b35, #f7931e)' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              💼 Work
            </button>
            <button
              onClick={() => setTab('findgf')}
              className="nav-button"
              style={{
                padding: '0.75rem 1.5rem',
                background: tab === 'findgf' ? 'linear-gradient(135deg, #ff6b35, #f7931e)' : 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              🔍 Find GF
            </button>
          </nav>

          {/* User Profile */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              background: '#ff6b35',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              transition: 'transform 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              A
            </div>
            <span style={{ fontSize: '0.9rem', color: '#ccc' }}>{userEmail || 'Guest'}</span>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div style={{
        display: 'flex',
        maxWidth: '1400px',
        margin: '0 auto',
        gap: '1.5rem',
        padding: '1.5rem 1.5rem 120px 1.5rem',
        minHeight: 'calc(100vh - 80px)'
      }}>
        {/* Left Sidebar - AdSense */}
        <div className="sidebar-ad" style={{
          width: '220px',
          flexShrink: 0
        }}>
          <div className="sidebar-card" style={{
            background: '#2a2a2a',
            borderRadius: '12px',
            border: '1px solid #333',
            overflow: 'hidden',
            height: 'fit-content',
            minHeight: '600px'
          }}>
            <div style={{
              padding: '1rem',
              borderBottom: '1px solid #333',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ fontSize: '0.8rem', color: '#ff6b35', fontWeight: '600' }}>📢 ADVERTISEMENT</span>
            </div>
            <div style={{
              padding: '1rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '500px'
            }}>
              <AdSense
                adSlot="1234567890"
                adFormat="vertical"
                style={{
                  width: '180px',
                  height: '600px'
                }}
                uniqueKey="left-sidebar"
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content-responsive" style={{ flex: 1, minWidth: 0 }}>
          {/* Generate Tab */}
          {tab === 'generate' && (
            <div className="content-section">
              {/* AI Image Generator Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  animation: 'pulse 2s infinite'
                }}>
                  🎨
                </div>
                <h2 style={{
                  margin: 0,
                  fontSize: '2rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  AI Image Generator
                </h2>
              </div>

              {/* Prompt Input */}
              <div style={{
                background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = 'rgba(255, 107, 53, 0.3)';
                e.target.style.boxShadow = '0 8px 32px rgba(255, 107, 53, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
              }}
              >
                <label style={{
                  display: 'block',
                  marginBottom: '1rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#ffffff'
                }}>
                  Describe your image:
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="A beautiful sunset over mountains with vibrant colors..."
                  style={{
                    width: '100%',
                    height: '120px',
                    padding: '1rem',
                    background: 'linear-gradient(145deg, #1a1a1a, #0f0f0f)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '1rem',
                    resize: 'none',
                    outline: 'none',
                    fontFamily: 'inherit',
                    transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ff6b35';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  onClick={generateImage}
                  disabled={loading || !prompt.trim()}
                  className="generate-button"
                  style={{
                    marginTop: '1.5rem',
                    padding: '1rem 2rem',
                    background: loading || !prompt.trim() ? '#444' : 'linear-gradient(135deg, #ff6b35, #f7931e)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: loading || !prompt.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {loading ? (
                    <>
                      <div className="spinner"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      ⭐ Generate Image
                    </>
                  )}
                </button>
              </div>

              {/* Generated Images */}
              <div>
                {/* Complete Image Gallery */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '1rem'
                }}>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '600',
                    margin: 0,
                    color: '#ffffff'
                  }}>
                    Complete Image Gallery ({totalImages})
                  </h3>
                  <button
                    onClick={fetchImages}
                    disabled={imagesLoading}
                    style={{
                      padding: '0.5rem 1rem',
                      background: imagesLoading 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'linear-gradient(135deg, #ff6b35, #f7931e)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: imagesLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      if (!imagesLoading) {
                        e.target.style.transform = 'scale(1.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    {imagesLoading ? (
                      <>
                        <div className="spinner" style={{ width: '16px', height: '16px', margin: 0 }}></div>
                        Refreshing...
                      </>
                    ) : (
                      <>
                        🔄 Refresh
                      </>
                    )}
                  </button>
                </div>

                {/* Debug information */}
                {process.env.NODE_ENV === 'development' && (
                  <div style={{
                    background: '#1a1a1a',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    fontSize: '0.8rem',
                    color: '#888'
                  }}>
                    {/* <strong>Debug Info:</strong><br/>
                    Loading: {imagesLoading ? 'Yes' : 'No'}<br/>
                    Images Array: {Array.isArray(images) ? 'Yes' : 'No'}<br/>
                    Images Length: {images?.length || 0}<br/>
                    Total Images: {totalImages}<br/>
                    Base URL: {BASE_URL} */}
                  </div>
                )}

                {/* Dynamic Images from State */}
                <div className="image-grid">
                  {imagesLoading ? (
                    // Loading skeleton cards
                    Array.from({ length: 8 }).map((_, index) => (
                      <div key={`skeleton-${index}`} className="image-card" style={{
                        background: '#2a2a2a',
                        borderRadius: '16px',
                        border: '1px solid #333',
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        animationDelay: `${index * 0.1}s`
                      }}>
                        {/* Skeleton Image */}
                        <div className="loading-skeleton" style={{
                          width: '100%',
                          height: '200px'
                        }} />
                        
                        {/* Skeleton Content */}
                        <div style={{ padding: '1.25rem' }}>
                          <div className="loading-skeleton" style={{
                            height: '12px',
                            borderRadius: '6px',
                            marginBottom: '0.5rem',
                            width: '60%'
                          }} />
                          <div className="loading-skeleton" style={{
                            height: '40px',
                            borderRadius: '6px',
                            marginBottom: '1rem'
                          }} />
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <div className="loading-skeleton" style={{
                              height: '24px',
                              borderRadius: '12px',
                              width: '80px'
                            }} />
                            <div className="loading-skeleton" style={{
                              height: '24px',
                              borderRadius: '12px',
                              width: '100px'
                            }} />
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    Array.isArray(images) && images.map((image, index) => {
                      const isExpanded = expandedDescriptions[index];
                      const shouldTruncate = image.prompt && image.prompt.length > 100;
                      
                      return (
                        <div key={index} className="image-card" style={{
                          background: '#2a2a2a',
                          borderRadius: '16px',
                          border: '1px solid #333',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                          animationDelay: `${index * 0.1}s`
                        }}>
                          {/* Image */}
                          <div style={{ position: 'relative', overflow: 'hidden' }}>
                            <img
                              src={image.url}
                              alt={image.prompt}
                              style={{
                                width: '100%',
                                height: '200px',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                console.log('Image failed to load:', image.url);
                                e.target.src = 'https://via.placeholder.com/300x200/333/fff?text=Image+Not+Found';
                              }}
                              onLoad={() => {
                                console.log('Image loaded successfully:', image.url);
                              }}
                            />
                            {/* Overlay gradient */}
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '60px',
                              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                              pointerEvents: 'none'
                            }} />
                          </div>
                          
                          {/* Content */}
                          <div style={{ padding: '1.25rem' }}>
                            {/* Prompt */}
                            <div style={{ marginBottom: '1rem' }}>
                              <p style={{
                                margin: '0 0 0.5rem 0',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                color: '#ff6b35',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Prompt
                              </p>
                              <p style={{
                                margin: 0,
                                fontSize: '0.9rem',
                                color: '#e0e0e0',
                                lineHeight: '1.5',
                                minHeight: isExpanded ? 'auto' : '3em',
                                overflow: 'hidden'
                              }}>
                                {isExpanded ? image.prompt : truncateText(image.prompt, 100)}
                              </p>
                              {shouldTruncate && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDescription(index);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#ff6b35',
                                    fontSize: '0.8rem',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    padding: '0.25rem 0',
                                    marginTop: '0.5rem',
                                    transition: 'color 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.color = '#f7931e';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.color = '#ff6b35';
                                  }}
                                >
                                  {isExpanded ? '← Show less' : 'View more →'}
                                </button>
                              )}
                            </div>
                            
                            {/* Tags */}
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              flexWrap: 'wrap',
                              alignItems: 'center'
                            }}>
                              <span style={{
                                padding: '0.4rem 0.8rem',
                                background: image.type === 'EDITED' 
                                  ? 'linear-gradient(135deg, #f7931e, #ff6b35)' 
                                  : 'linear-gradient(135deg, #ff6b35, #f7931e)',
                                borderRadius: '20px',
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                color: '#ffffff',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
                                transition: 'transform 0.2s ease'
                              }}
                              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                              >
                                {image.type === 'EDITED' ? '✏️ Edited' : '🎨 Generated'}
                              </span>
                              <span style={{
                                padding: '0.4rem 0.8rem',
                                background: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: '20px',
                                fontSize: '0.7rem',
                                color: '#ccc',
                                fontWeight: '500',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                              }}>
                                📅 {new Date(image.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {!imagesLoading && (!Array.isArray(images) || images.length === 0) && (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem',
                    color: '#666',
                    animation: 'fadeInUp 0.6s ease-out'
                  }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      background: '#333',
                      borderRadius: '50%',
                      margin: '0 auto 1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2rem',
                      transition: 'transform 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.1) rotate(10deg)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1) rotate(0deg)'}
                    >
                      🎨
                    </div>
                    <h3 style={{
                      margin: '0 0 0.5rem 0',
                      fontSize: '1.2rem',
                      color: '#ccc'
                    }}>
                      No images yet
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>
                      Create your first AI-generated image above!
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Edit Tab */}
          {tab === 'edit' && (
            <div className="content-section">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  animation: 'pulse 2s infinite'
                }}>
                  ✏️
                </div>
                <h2 style={{
                  margin: 0,
                  fontSize: '2rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Image Editor
                </h2>
              </div>

              <div style={{
                background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = 'rgba(255, 107, 53, 0.3)';
                e.target.style.boxShadow = '0 8px 32px rgba(255, 107, 53, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
              }}
              >
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#ffffff'
                  }}>
                    Upload Image:
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setEditFile(file);
                        setImagePreview(URL.createObjectURL(file));
                      } else {
                        setEditFile(null);
                        setImagePreview(null);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'linear-gradient(145deg, #1a1a1a, #0f0f0f)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '0.9rem',
                      transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#ff6b35';
                      e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <div style={{
                    marginTop: '0.5rem',
                    fontSize: '0.8rem',
                    color: '#888',
                    lineHeight: '1.4'
                  }}>
                    💡 <strong>Tip:</strong> Upload any image (JPG, PNG, GIF) and describe how you want to edit it. 
                    The AI will modify your image based on your description.
                  </div>
                </div>

                {imagePreview && (
                  <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
                    <img
                      src={imagePreview}
                      alt="Uploaded preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        transition: 'transform 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    />
                    <button
                      onClick={() => {
                        setImagePreview(null);
                        setEditFile(null);
                        // Reset the file input
                        const fileInput = document.querySelector('input[type="file"]');
                        if (fileInput) fileInput.value = '';
                      }}
                      style={{
                        marginTop: '0.5rem',
                        padding: '0.5rem 1rem',
                        background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'transform 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    >
                      🗑️ Clear Image
                    </button>
                  </div>
                )}

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#ffffff'
                  }}>
                    Describe how to edit the image:
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Add a beautiful girl with this bird..."
                    style={{
                      width: '100%',
                      height: '120px',
                      padding: '1rem',
                      background: '#1a1a1a',
                      border: '1px solid #444',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '1rem',
                      resize: 'none',
                      outline: 'none',
                      fontFamily: 'inherit',
                      transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#ff6b35';
                      e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#444';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <button
                  onClick={editImage}
                  disabled={loading || !editFile || !prompt.trim()}
                  className="generate-button"
                  style={{
                    padding: '1rem 2rem',
                    background: loading || !editFile || !prompt.trim() ? '#444' : 'linear-gradient(135deg, #ff6b35, #f7931e)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: loading || !editFile || !prompt.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {loading ? (
                    <>
                      <div className="spinner"></div>
                      Editing...
                    </>
                  ) : (
                    <>
                      ✨ Edit Image
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Karaoke Tab */}
          {tab === 'karaoke' && (
            <div className="content-section">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  animation: 'pulse 2s infinite'
                }}>
                  🎵
                </div>
                <h2 style={{
                  margin: 0,
                  fontSize: '2rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Karaoke Studio
                </h2>
              </div>

              {/* Search Section */}
              <div style={{
                background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = 'rgba(255, 107, 53, 0.3)';
                e.target.style.boxShadow = '0 8px 32px rgba(255, 107, 53, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
              }}
              >
                <label style={{
                  display: 'block',
                  marginBottom: '1rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#ffffff'
                }}>
                  🎤 Search for a song to sing:
                </label>
                
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <input
                    id="searchInput"
                    type="text"
                    placeholder="e.g., Bohemian Rhapsody, Taylor Swift, Imagine Dragons..."
                    style={{
                      flex: 1,
                      padding: '1rem',
                      background: 'linear-gradient(145deg, #1a1a1a, #0f0f0f)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#ff6b35';
                      e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1), inset 0 2px 8px rgba(255, 107, 53, 0.05)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                  <button
                    onClick={handleSearch}
                    disabled={searchLoading}
                    className="generate-button"
                    style={{
                      padding: '1rem 2rem',
                      background: searchLoading ? '#444' : 'linear-gradient(135deg, #ff6b35, #f7931e)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: searchLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      minWidth: '140px'
                    }}
                  >
                    {searchLoading ? (
                      <>
                        <div className="spinner"></div>
                        Searching...
                      </>
                    ) : (
                      <>
                        🔍 Find Song
                      </>
                    )}
                  </button>
                </div>

                {/* Quick Examples */}
                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    fontSize: '0.8rem',
                    color: '#888',
                    marginRight: '0.5rem'
                  }}>
                    Try these:
                  </span>
                  {['Bohemian Rhapsody', 'Shape of You', 'Imagine', 'Billie Jean'].map((example, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        document.getElementById('searchInput').value = example;
                        handleSearch();
                      }}
                      style={{
                        padding: '0.25rem 0.75rem',
                        background: 'rgba(255, 107, 53, 0.1)',
                        border: '1px solid rgba(255, 107, 53, 0.3)',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        color: '#ff6b35',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 107, 53, 0.2)';
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 107, 53, 0.1)';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>

              {/* Instructions */}
              <div style={{
                background: 'rgba(255, 107, 53, 0.1)',
                border: '1px solid rgba(255, 107, 53, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{
                  margin: '0 0 1rem 0',
                  color: '#ff6b35',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  🎤 How Karaoke Studio Works:
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem'
                  }}>
                    <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>🔍</span>
                    <div>
                      <strong style={{ color: '#ff6b35', fontSize: '0.9rem' }}>Search</strong>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#ccc', lineHeight: '1.4' }}>
                        Enter any song title, artist name, or both
                      </p>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem'
                  }}>
                    <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>🎥</span>
                    <div>
                      <strong style={{ color: '#ff6b35', fontSize: '0.9rem' }}>Watch</strong>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#ccc', lineHeight: '1.4' }}>
                        We find the YouTube video automatically
                      </p>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem'
                  }}>
                    <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>🎵</span>
                    <div>
                      <strong style={{ color: '#ff6b35', fontSize: '0.9rem' }}>Sing</strong>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#ccc', lineHeight: '1.4' }}>
                        Follow along with synchronized lyrics
                      </p>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem'
                  }}>
                    <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>🎮</span>
                    <div>
                      <strong style={{ color: '#ff6b35', fontSize: '0.9rem' }}>Control</strong>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#ccc', lineHeight: '1.4' }}>
                        Use the floating player to control playback
                      </p>
                    </div>
                  </div>
                </div>
                
                <div style={{
                  background: 'rgba(255, 107, 53, 0.05)',
                  border: '1px solid rgba(255, 107, 53, 0.1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginTop: '1rem'
                }}>
                  <h5 style={{
                    margin: '0 0 0.5rem 0',
                    color: '#ff6b35',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}>
                    💡 Pro Tips:
                  </h5>
                  <ul style={{
                    margin: 0,
                    paddingLeft: '1.2rem',
                    fontSize: '0.8rem',
                    color: '#ccc',
                    lineHeight: '1.6'
                  }}>
                    <li>Try searching with just the song title for better results</li>
                    <li>Include the artist name if you get too many results</li>
                    <li>The system works with songs in any language</li>
                    <li>Use the floating player to minimize while browsing other tabs</li>
                  </ul>
                </div>
              </div>

              {/* Status Section */}
              {(title || searchLoading) && (
                <div style={{
                  background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  padding: '1.5rem',
                  textAlign: 'center'
                }}>
                  {searchLoading ? (
                    <div>
                      <div className="spinner" style={{
                        width: '32px',
                        height: '32px',
                        margin: '0 auto 1rem',
                        borderWidth: '3px'
                      }}></div>
                      <h3 style={{
                        margin: '0 0 0.5rem 0',
                        color: '#ff6b35',
                        fontSize: '1.2rem'
                      }}>
                        🔍 Searching for your song...
                      </h3>
                      <p style={{
                        margin: 0,
                        color: '#ccc',
                        fontSize: '0.9rem'
                      }}>
                        Finding the perfect video and preparing lyrics
                      </p>
                    </div>
                  ) : title ? (
                    <div>
                      <h3 style={{
                        margin: '0 0 0.5rem 0',
                        color: '#ff6b35',
                        fontSize: '1.2rem'
                      }}>
                        🎉 Ready to Sing!
                      </h3>
                      <p style={{
                        margin: '0 0 1rem 0',
                        color: '#ffffff',
                        fontSize: '1rem',
                        fontWeight: '500'
                      }}>
                        {title}
                      </p>
                      <p style={{
                        margin: 0,
                        color: '#ccc',
                        fontSize: '0.9rem'
                      }}>
                        Check the floating player in the bottom-right corner!
                      </p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {/* Shorts Tab */}
          {tab === 'shorts' && (
            <div className="content-section">
              {/* Shorts Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  animation: 'pulse 2s infinite'
                }}>
                  📱
                </div>
                <h2 style={{
                  margin: 0,
                  fontSize: '2rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Short Video Studio
                </h2>
              </div>

              {/* Debug Info */}
              <div style={{
                background: '#1a1a1a',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                fontSize: '0.8rem',
                color: '#888',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                {/* <strong style={{ color: '#ff6b35' }}>Debug Info:</strong><br/>
                User Registered: {userRegistered ? '✅ Yes' : '❌ No'}<br/>
                User Email: {userEmail}<br/>
                Base URL: {BASE_URL}<br/>
                Registration Loading: {registrationLoading ? 'Yes' : 'No'} */}
              </div>

              {/* Video Generation Form */}
              <div style={{
                background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = 'rgba(255, 107, 53, 0.3)';
                e.target.style.boxShadow = '0 8px 32px rgba(255, 107, 53, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
              }}
              >
                <label style={{
                  display: 'block',
                  marginBottom: '1rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#ffffff'
                }}>
                  📱 Describe your short video idea:
                </label>
                
                <textarea
                  value={shortVideoPrompt}
                  onChange={(e) => setShortVideoPrompt(e.target.value)}
                  placeholder="A quick cooking tip, funny pet moment, motivational quote, dance trend, or any creative idea..."
                  style={{
                    width: '100%',
                    height: '100px',
                    padding: '1rem',
                    background: 'linear-gradient(145deg, #1a1a1a, #0f0f0f)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '1rem',
                    resize: 'none',
                    outline: 'none',
                    fontFamily: 'inherit',
                    transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ff6b35';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.boxShadow = 'none';
                  }}
                />

                {/* Video Settings */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginTop: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  {/* Duration */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#ccc'
                    }}>
                      Duration (seconds):
                    </label>
                    <select
                      value={shortVideoDuration}
                      onChange={(e) => setShortVideoDuration(parseInt(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'linear-gradient(145deg, #1a1a1a, #0f0f0f)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        outline: 'none'
                      }}
                    >
                      <option value={5} style={{ background: '#1a1a1a', color: '#ffffff' }}>5 seconds</option>
                    </select>
                  </div>

                  {/* Language Selection - NEW */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#ccc'
                    }}>
                      Language:
                    </label>
                    <select
                      value={shortVideoLanguage}
                      onChange={(e) => setShortVideoLanguage(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'linear-gradient(145deg, #1a1a1a, #0f0f0f)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        outline: 'none'
                      }}
                    >
                      <option value="english" style={{ background: '#1a1a1a', color: '#ffffff' }}>🇺🇸 English</option>
                      <option value="tagalog" style={{ background: '#1a1a1a', color: '#ffffff' }}>🇵🇭 Tagalog</option>
                    </select>
                  </div>

                  {/* Style */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#ccc'
                    }}>
                      Video Style:
                    </label>
                    <select
                      value={shortVideoStyle}
                      onChange={(e) => setShortVideoStyle(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'linear-gradient(145deg, #1a1a1a, #0f0f0f)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        outline: 'none'
                      }}
                    >
                      <option value="trendy" style={{ background: '#1a1a1a', color: '#ffffff' }}>🔥 Trendy</option>
                      <option value="professional" style={{ background: '#1a1a1a', color: '#ffffff' }}>💼 Professional</option>
                      <option value="fun" style={{ background: '#1a1a1a', color: '#ffffff' }}>🎉 Fun & Playful</option>
                      <option value="educational" style={{ background: '#1a1a1a', color: '#ffffff' }}>📚 Educational</option>
                      <option value="cinematic" style={{ background: '#1a1a1a', color: '#ffffff' }}>🎬 Cinematic</option>
                      <option value="minimalist" style={{ background: '#1a1a1a', color: '#ffffff' }}>✨ Minimalist</option>
                    </select>
                  </div>

                  {/* Platform */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#ccc'
                    }}>
                      Target Platform:
                    </label>
                    <select
                      value={shortVideoPlatform}
                      onChange={(e) => setShortVideoPlatform(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'linear-gradient(145deg, #1a1a1a, #0f0f0f)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        outline: 'none'
                      }}
                    >
                      <option value="tiktok" style={{ background: '#1a1a1a', color: '#ffffff' }}>📱 TikTok</option>
                      <option value="instagram" style={{ background: '#1a1a1a', color: '#ffffff' }}>📸 Instagram Reels</option>
                      <option value="youtube" style={{ background: '#1a1a1a', color: '#ffffff' }}>🎥 YouTube Shorts</option>
                      <option value="general" style={{ background: '#1a1a1a', color: '#ffffff' }}>🌐 General</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={generateShortVideo}
                  disabled={shortVideoLoading || !shortVideoPrompt.trim()}
                  className="generate-button"
                  style={{
                    padding: '1rem 2rem',
                    background: shortVideoLoading || !shortVideoPrompt.trim() ? '#444' : 'linear-gradient(135deg, #ff6b35, #f7931e)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: shortVideoLoading || !shortVideoPrompt.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {shortVideoLoading ? (
                    <>
                      <div className="spinner"></div>
                      Generating Video...
                    </>
                  ) : (
                    <>
                      🎬 Generate Short Video
                    </>
                  )}
                </button>
              </div>

              {/* Generated Video Preview */}
              {generatedShortVideo && (
                <div style={{
                  background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  padding: '1.5rem',
                  marginBottom: '1.5rem',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                  <h3 style={{
                    margin: '0 0 1rem 0',
                    fontSize: '1.3rem',
                    color: '#ffffff',
                    fontWeight: '600'
                  }}>
                    🎉 Your Short Video is Ready!
                  </h3>
                  
                  <div style={{
                    background: '#1a1a1a',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#ff6b35' }}>
                      {generatedShortVideo.title}
                    </h4>
                    <p style={{ margin: '0 0 1rem 0', color: '#ccc', fontSize: '0.9rem' }}>
                      Duration: {generatedShortVideo.duration}s | Created: {new Date(generatedShortVideo.createdAt).toLocaleDateString()}
                    </p>
                    
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => openVideoModal(generatedShortVideo.videoUrl, generatedShortVideo.title)}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        ▶️ Watch Video
                      </button>
                      
                      <a
                        href={`${BASE_URL}/api/download-video/${generatedShortVideo.fileName}`}
                        download
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          color: '#ffffff',
                          textDecoration: 'none',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        💾 Download
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Video Gallery */}
              <div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  🎬 Your Short Videos ({userShortVideos.length})
                </h3>

                {userShortVideos.length === 0 ? (
                  <div style={{
                    background: 'rgba(255, 107, 53, 0.1)',
                    border: '1px solid rgba(255, 107, 53, 0.2)',
                    borderRadius: '12px',
                    padding: '2rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📱</div>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#ff6b35' }}>
                      No short videos yet
                    </h4>
                    <p style={{ margin: 0, color: '#ccc' }}>
                      Create your first short video using the form above!
                    </p>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '1.5rem'
                  }}>
                    {userShortVideos.map((video, index) => (
                      <div key={video.id || index} style={{
                        background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 12px 24px rgba(255, 107, 53, 0.2)';
                        e.currentTarget.style.borderColor = 'rgba(255, 107, 53, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      }}
                      >
                        {/* Video Thumbnail/Preview */}
                        <div style={{
                          height: '180px',
                          background: 'linear-gradient(135deg, #1e3c72, #2a5298)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative'
                        }}>
                          <div style={{
                            fontSize: '3rem',
                            opacity: 0.7
                          }}>
                            📱
                          </div>
                          
                          {/* Duration Badge */}
                          <div style={{
                            position: 'absolute',
                            top: '0.75rem',
                            right: '0.75rem',
                            background: 'rgba(0, 0, 0, 0.8)',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.8rem',
                            color: '#ffffff',
                            fontWeight: '600'
                          }}>
                            {video.duration}s
                          </div>
                          
                          {/* Status Badge */}
                          <div style={{
                            position: 'absolute',
                            top: '0.75rem',
                            left: '0.75rem',
                            background: video.status === 'completed' 
                              ? 'rgba(76, 175, 80, 0.9)' 
                              : video.status === 'processing'
                              ? 'rgba(255, 193, 7, 0.9)'
                              : 'rgba(244, 67, 54, 0.9)',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.7rem',
                            color: '#ffffff',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            {video.status === 'completed' ? '✅' : video.status === 'processing' ? '⏳' : '❌'}
                          </div>
                          
                          {/* Format Badge */}
                          {video.status === 'completed' && (
                            <div style={{
                              position: 'absolute',
                              bottom: '0.75rem',
                              left: '0.75rem',
                              background: 'rgba(0, 0, 0, 0.8)',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '12px',
                              fontSize: '0.6rem',
                              color: '#ffffff',
                              fontWeight: '600'
                            }}>
                              🎬 MP4
                            </div>
                          )}
                        </div>
                        
                        {/* Video Info */}
                        <div style={{ padding: '1rem' }}>
                          <h4 style={{
                            margin: '0 0 0.5rem 0',
                            fontSize: '1rem',
                            color: '#ffffff',
                            fontWeight: '600',
                            lineHeight: '1.3'
                          }}>
                            {video.title}
                          </h4>
                          
                          <p style={{
                            margin: '0 0 1rem 0',
                            fontSize: '0.8rem',
                            color: '#888',
                            lineHeight: '1.4'
                          }}>
                            {video.script ? video.script.substring(0, 80) + '...' : 'No script available'}
                          </p>
                          
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '0.7rem',
                            color: '#666'
                          }}>
                            <span>
                              {new Date(video.createdAt).toLocaleDateString()}
                            </span>
                            <span>
                              {video.fileSize ? `${(video.fileSize / 1024 / 1024).toFixed(1)}MB` : 'Unknown size'}
                            </span>
                          </div>
                          
                          {/* Action Buttons */}
                          {video.status === 'completed' && (
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              marginTop: '1rem'
                            }}>
                              <button
                                onClick={() => openVideoModal(video.videoUrl, video.title)}
                                style={{
                                  flex: 1,
                                  padding: '0.5rem',
                                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  color: '#ffffff',
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'translateY(-1px)';
                                  e.target.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }}
                              >
                                ▶️ Watch
                              </button>
                              
                              <button
                                onClick={async () => {
                                  try {
                                    const response = await fetch(`${BASE_URL}/api/download-video/${video.fileName}`);
                                    
                                    if (response.ok) {
                                      // If successful, trigger download
                                      const blob = await response.blob();
                                      const url = window.URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = video.fileName;
                                      document.body.appendChild(a);
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      document.body.removeChild(a);
                                    } else {
                                      // Handle error (likely HTML video)
                                      const errorData = await response.json();
                                      if (errorData.isHTMLVideo) {
                                        alert('This video is in HTML format and can only be viewed online. Please use the Watch button to view it.');
                                      } else {
                                        alert('Download failed: ' + (errorData.error || 'Unknown error'));
                                      }
                                    }
                                  } catch (error) {
                                    console.error('Download error:', error);
                                    alert('Download failed. Please try again.');
                                  }
                                }}
                                style={{
                                  flex: 1,
                                  padding: '0.5rem',
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  borderRadius: '6px',
                                  color: '#ffffff',
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                                  e.target.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                                  e.target.style.transform = 'translateY(0)';
                                }}
                              >
                                💾 Download
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Tips Section */}
              <div style={{
                background: 'rgba(255, 107, 53, 0.1)',
                border: '1px solid rgba(255, 107, 53, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginTop: '2rem'
              }}>
                <h4 style={{
                  margin: '0 0 1rem 0',
                  color: '#ff6b35',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  💡 Short Video Tips:
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem',
                  fontSize: '0.9rem',
                  color: '#ccc'
                }}>
                  <div>
                    <strong style={{ color: '#ff6b35' }}>📱 TikTok:</strong> Trendy, fast-paced, vertical format. Use popular sounds and hashtags.
                  </div>
                  <div>
                    <strong style={{ color: '#ff6b35' }}>📸 Instagram:</strong> High-quality visuals, engaging captions, story-driven content.
                  </div>
                  <div>
                    <strong style={{ color: '#ff6b35' }}>🎥 YouTube:</strong> Educational or entertaining, clear audio, strong hook in first 3 seconds.
                  </div>
                  <div>
                    <strong style={{ color: '#ff6b35' }}>⏱️ Duration:</strong> Shorter videos (5-15s) often perform better for engagement.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Work Tab */}
          {tab === 'work' && (
            <div className="content-section">
              {console.log('Work tab is rendering with projects:', workProjects.length)}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  animation: 'pulse 2s infinite'
                }}>
                  💼
                </div>
                <h2 style={{
                  margin: 0,
                  fontSize: '2rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Work Portfolio
                </h2>
              </div>

              {/* Portfolio Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  color: '#ffffff'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {workProjects.length}
                  </div>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Total Projects</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  color: '#ffffff'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {workProjects.filter(p => p.status === 'Completed').length}
                  </div>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Completed</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #2196F3, #1976D2)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  color: '#ffffff'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {workProjects.filter(p => p.status === 'In Progress').length}
                  </div>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>In Progress</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #9C27B0, #7B1FA2)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  color: '#ffffff'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {Math.round(workProjects.reduce((acc, p) => acc + p.progress, 0) / workProjects.length)}%
                  </div>
                  <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>Avg Progress</div>
                </div>
              </div>

              {/* Portfolio Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '2rem',
                marginBottom: '2rem'
              }}>
                {workProjects.map((project, index) => (
                  <div key={project.id} style={{
                    background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(255, 107, 53, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(255, 107, 53, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  }}
                  >
                    {/* Project Image */}
                    <div style={{
                      height: '200px',
                      backgroundImage: `url(${project.image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      {/* Overlay */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
                        display: 'flex',
                        alignItems: 'flex-end',
                        padding: '1rem'
                      }}>
                        {/* Status Badge */}
                        <div style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                          padding: '0.5rem 1rem',
                          background: project.status === 'Completed' 
                            ? 'rgba(76, 175, 80, 0.9)' 
                            : 'rgba(255, 193, 7, 0.9)',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#ffffff',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          backdropFilter: 'blur(10px)'
                        }}>
                          {project.status === 'Completed' ? '✅ Completed' : '🔄 In Progress'}
                        </div>
                        
                        {/* Project Duration */}
                        <div style={{
                          background: 'rgba(0, 0, 0, 0.7)',
                          padding: '0.5rem 1rem',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          color: '#ffffff',
                          backdropFilter: 'blur(10px)'
                        }}>
                          {project.startDate} - {project.endDate}
                        </div>
                      </div>
                    </div>
                    
                    {/* Project Content */}
                    <div style={{ padding: '1.5rem' }}>
                      <h3 style={{
                        margin: '0 0 0.75rem 0',
                        fontSize: '1.3rem',
                        color: '#ffffff',
                        fontWeight: '700'
                      }}>
                        {project.title}
                      </h3>
                      
                      <p style={{
                        margin: '0 0 1rem 0',
                        fontSize: '0.9rem',
                        color: '#ccc',
                        lineHeight: '1.6'
                      }}>
                        {project.description}
                      </p>

                      {/* Key Features */}
                      <div style={{ marginBottom: '1rem' }}>
                        <h4 style={{
                          margin: '0 0 0.5rem 0',
                          fontSize: '0.9rem',
                          color: '#ff6b35',
                          fontWeight: '600'
                        }}>
                          🚀 Key Features
                        </h4>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.5rem'
                        }}>
                          {project.features.map((feature, featureIndex) => (
                            <span key={featureIndex} style={{
                              padding: '0.25rem 0.75rem',
                              background: 'rgba(255, 107, 53, 0.1)',
                              border: '1px solid rgba(255, 107, 53, 0.2)',
                              borderRadius: '12px',
                              fontSize: '0.7rem',
                              color: '#ff6b35',
                              fontWeight: '500'
                            }}>
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.5rem'
                        }}>
                          <span style={{ fontSize: '0.8rem', color: '#ff6b35', fontWeight: '600' }}>
                            Progress
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#ffffff', fontWeight: '600' }}>
                            {project.progress}%
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${project.progress}%`,
                            height: '100%',
                            background: project.progress === 100 
                              ? 'linear-gradient(90deg, #4CAF50, #45a049)'
                              : 'linear-gradient(90deg, #ff6b35, #f7931e)',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>
                      
                      {/* Tech Stack */}
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{
                          margin: '0 0 0.5rem 0',
                          fontSize: '0.9rem',
                          color: '#ff6b35',
                          fontWeight: '600'
                        }}>
                          🛠️ Tech Stack
                        </h4>
                        <div style={{
                          display: 'flex',
                          gap: '0.5rem',
                          flexWrap: 'wrap'
                        }}>
                          {project.tech.map((tech, techIndex) => (
                            <span key={techIndex} style={{
                              padding: '0.3rem 0.8rem',
                              background: techIndex % 3 === 0 
                                ? 'rgba(97, 218, 251, 0.2)' 
                                : techIndex % 3 === 1 
                                ? 'rgba(104, 201, 176, 0.2)'
                                : 'rgba(156, 39, 176, 0.2)',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              color: techIndex % 3 === 0 
                                ? '#61dafb' 
                                : techIndex % 3 === 1 
                                ? '#68c9b0'
                                : '#9c27b0',
                              border: techIndex % 3 === 0 
                                ? '1px solid rgba(97, 218, 251, 0.3)' 
                                : techIndex % 3 === 1 
                                ? '1px solid rgba(104, 201, 176, 0.3)'
                                : '1px solid rgba(156, 39, 176, 0.3)',
                              fontWeight: '500',
                              transition: 'transform 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div style={{
                        display: 'flex',
                        gap: '0.75rem',
                        marginTop: '1rem'
                      }}>
                        <button
                          style={{
                            flex: 1,
                            padding: '0.75rem 1rem',
                            background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                            border: 'none',
                            borderRadius: '10px',
                            color: '#ffffff',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(project.demoUrl, '_blank');
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 8px 20px rgba(255, 107, 53, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          🚀 Live Demo
                        </button>
                        
                        <button
                          style={{
                            flex: 1,
                            padding: '0.75rem 1rem',
                            background: 'linear-gradient(135deg, #333, #24292e)',
                            border: 'none',
                            borderRadius: '10px',
                            color: '#ffffff',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(project.githubUrl, '_blank');
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #444, #333)';
                            e.target.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #333, #24292e)';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          📂 GitHub
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add New Project Button */}
              <div style={{
                textAlign: 'center',
                marginBottom: '2rem'
              }}>
                <button
                  style={{
                    padding: '1rem 2rem',
                    background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(255, 107, 53, 0.3)',
                    marginRight: '1rem'
                  }}
                  onClick={() => setShowAddProjectModal(true)}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-3px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(255, 107, 53, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(255, 107, 53, 0.3)';
                  }}
                >
                  ➕ Add New Project
                </button>
                
                {/* Debug Button */}
                <button
                  style={{
                    padding: '1rem 2rem',
                    background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)'
                  }}
                  onClick={() => {
                    console.log('Debug: Current tab:', tab);
                    console.log('Debug: Work projects:', workProjects);
                    console.log('Debug: Show modal:', showAddProjectModal);
                    alert(`Debug Info:\nCurrent tab: ${tab}\nProjects count: ${workProjects.length}\nModal state: ${showAddProjectModal}`);
                  }}
                >
                  🐛 Debug Info
                </button>
              </div>

              {/* Add Project Modal */}
              {showAddProjectModal && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  padding: '2rem'
                }}>
                  <div style={{
                    background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    padding: '2rem',
                    maxWidth: '600px',
                    width: '100%',
                    maxHeight: '80vh',
                    overflowY: 'auto'
                  }}>
                    <h3 style={{
                      margin: '0 0 1.5rem 0',
                      fontSize: '1.5rem',
                      color: '#ffffff',
                      textAlign: 'center'
                    }}>
                      ➕ Add New Project
                    </h3>

                    <div style={{ display: 'grid', gap: '1rem' }}>
                      {/* Project Title */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ff6b35', fontWeight: '600' }}>
                          Project Title *
                        </label>
                        <input
                          type="text"
                          value={newProject.title}
                          onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            color: '#ffffff',
                            fontSize: '1rem'
                          }}
                          placeholder="Enter project title"
                        />
                      </div>

                      {/* Project Description */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ff6b35', fontWeight: '600' }}>
                          Description *
                        </label>
                        <textarea
                          value={newProject.description}
                          onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            color: '#ffffff',
                            fontSize: '1rem',
                            minHeight: '100px',
                            resize: 'vertical'
                          }}
                          placeholder="Describe your project"
                        />
                      </div>

                      {/* Status and Progress */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ff6b35', fontWeight: '600' }}>
                            Status
                          </label>
                          <select
                            value={newProject.status}
                            onChange={(e) => setNewProject({...newProject, status: e.target.value})}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '8px',
                              color: '#ffffff',
                              fontSize: '1rem'
                            }}
                          >
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ff6b35', fontWeight: '600' }}>
                            Progress (%)
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={newProject.progress}
                            onChange={(e) => setNewProject({...newProject, progress: parseInt(e.target.value) || 0})}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '8px',
                              color: '#ffffff',
                              fontSize: '1rem'
                            }}
                          />
                        </div>
                      </div>

                      {/* Tech Stack */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ff6b35', fontWeight: '600' }}>
                          Tech Stack (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={newProject.tech.join(', ')}
                          onChange={(e) => setNewProject({...newProject, tech: e.target.value.split(',').map(t => t.trim())})}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            color: '#ffffff',
                            fontSize: '1rem'
                          }}
                          placeholder="React, Node.js, MongoDB, etc."
                        />
                      </div>

                      {/* Features */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ff6b35', fontWeight: '600' }}>
                          Key Features (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={newProject.features.join(', ')}
                          onChange={(e) => setNewProject({...newProject, features: e.target.value.split(',').map(f => f.trim())})}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            color: '#ffffff',
                            fontSize: '1rem'
                          }}
                          placeholder="User Authentication, Real-time Updates, etc."
                        />
                      </div>

                      {/* URLs */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ff6b35', fontWeight: '600' }}>
                            Demo URL
                          </label>
                          <input
                            type="url"
                            value={newProject.demoUrl}
                            onChange={(e) => setNewProject({...newProject, demoUrl: e.target.value})}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '8px',
                              color: '#ffffff',
                              fontSize: '1rem'
                            }}
                            placeholder="https://demo.com"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ff6b35', fontWeight: '600' }}>
                            GitHub URL
                          </label>
                          <input
                            type="url"
                            value={newProject.githubUrl}
                            onChange={(e) => setNewProject({...newProject, githubUrl: e.target.value})}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '8px',
                              color: '#ffffff',
                              fontSize: '1rem'
                            }}
                            placeholder="https://github.com/..."
                          />
                        </div>
                      </div>

                      {/* Dates */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ff6b35', fontWeight: '600' }}>
                            Start Date
                          </label>
                          <input
                            type="text"
                            value={newProject.startDate}
                            onChange={(e) => setNewProject({...newProject, startDate: e.target.value})}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '8px',
                              color: '#ffffff',
                              fontSize: '1rem'
                            }}
                            placeholder="2024-01"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ff6b35', fontWeight: '600' }}>
                            End Date
                          </label>
                          <input
                            type="text"
                            value={newProject.endDate}
                            onChange={(e) => setNewProject({...newProject, endDate: e.target.value})}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '8px',
                              color: '#ffffff',
                              fontSize: '1rem'
                            }}
                            placeholder="2024-03"
                          />
                        </div>
                      </div>

                      {/* Image URL */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.5rem', color: '#ff6b35', fontWeight: '600' }}>
                          Project Image URL
                        </label>
                        <input
                          type="url"
                          value={newProject.image}
                          onChange={(e) => setNewProject({...newProject, image: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            color: '#ffffff',
                            fontSize: '1rem'
                          }}
                          placeholder="https://via.placeholder.com/400x250/..."
                        />
                      </div>
                    </div>

                    {/* Modal Actions */}
                    <div style={{
                      display: 'flex',
                      gap: '1rem',
                      marginTop: '2rem',
                      justifyContent: 'flex-end'
                    }}>
                      <button
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '1rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => setShowAddProjectModal(false)}
                        onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                        onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                      >
                        Cancel
                      </button>
                      <button
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '1rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={addNewProject}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 8px 20px rgba(255, 107, 53, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        Add Project
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Skills Section */}
              <div style={{
                background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                padding: '2rem',
                marginBottom: '2rem'
              }}>
                <h3 style={{
                  margin: '0 0 1.5rem 0',
                  fontSize: '1.5rem',
                  color: '#ffffff',
                  textAlign: 'center'
                }}>
                  🛠️ Technical Skills
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1.5rem'
                }}>
                  {/* Frontend */}
                  <div>
                    <h4 style={{
                      margin: '0 0 1rem 0',
                      fontSize: '1.1rem',
                      color: '#ff6b35',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      🎨 Frontend
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {['React', 'Vue.js', 'TypeScript', 'Tailwind CSS', 'Next.js'].map((skill, index) => (
                        <span key={index} style={{
                          padding: '0.4rem 0.8rem',
                          background: 'rgba(97, 218, 251, 0.2)',
                          borderRadius: '16px',
                          fontSize: '0.8rem',
                          color: '#61dafb',
                          border: '1px solid rgba(97, 218, 251, 0.3)',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(97, 218, 251, 0.3)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(97, 218, 251, 0.2)';
                          e.target.style.transform = 'scale(1)';
                        }}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Backend */}
                  <div>
                    <h4 style={{
                      margin: '0 0 1rem 0',
                      fontSize: '1.1rem',
                      color: '#ff6b35',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      ⚙️ Backend
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {['Node.js', 'Python', 'Express', 'FastAPI', 'MongoDB'].map((skill, index) => (
                        <span key={index} style={{
                          padding: '0.4rem 0.8rem',
                          background: 'rgba(104, 201, 176, 0.2)',
                          borderRadius: '16px',
                          fontSize: '0.8rem',
                          color: '#68c9b0',
                          border: '1px solid rgba(104, 201, 176, 0.3)',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(104, 201, 176, 0.3)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(104, 201, 176, 0.2)';
                          e.target.style.transform = 'scale(1)';
                        }}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* AI/ML */}
                  <div>
                    <h4 style={{
                      margin: '0 0 1rem 0',
                      fontSize: '1.1rem',
                      color: '#ff6b35',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      🤖 AI/ML
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {['OpenAI', 'TensorFlow', 'PyTorch', 'Hugging Face', 'LangChain'].map((skill, index) => (
                        <span key={index} style={{
                          padding: '0.4rem 0.8rem',
                          background: 'rgba(255, 107, 53, 0.2)',
                          borderRadius: '16px',
                          fontSize: '0.8rem',
                          color: '#ff6b35',
                          border: '1px solid rgba(255, 107, 53, 0.3)',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(255, 107, 53, 0.3)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(255, 107, 53, 0.2)';
                          e.target.style.transform = 'scale(1)';
                        }}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Tools */}
                  <div>
                    <h4 style={{
                      margin: '0 0 1rem 0',
                      fontSize: '1.1rem',
                      color: '#ff6b35',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      🔧 Tools
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {['Git', 'Docker', 'AWS', 'Vercel', 'Figma'].map((skill, index) => (
                        <span key={index} style={{
                          padding: '0.4rem 0.8rem',
                          background: 'rgba(156, 39, 176, 0.2)',
                          borderRadius: '16px',
                          fontSize: '0.8rem',
                          color: '#9c27b0',
                          border: '1px solid rgba(156, 39, 176, 0.3)',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(156, 39, 176, 0.3)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(156, 39, 176, 0.2)';
                          e.target.style.transform = 'scale(1)';
                        }}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Contact Section */}
              <div style={{
                background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                padding: '2rem',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  borderRadius: '50%',
                  margin: '0 auto 1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  animation: 'pulse 2s infinite'
                }}>
                  🤝
                </div>
                
                <h3 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.8rem',
                  color: '#ffffff'
                }}>
                  Let's Build Something Amazing Together
                </h3>
                
                <p style={{
                  margin: '0 0 2rem 0',
                  fontSize: '1.1rem',
                  color: '#ccc',
                  maxWidth: '700px',
                  margin: '0 auto 2rem auto',
                  lineHeight: '1.6'
                }}>
                  I'm passionate about creating innovative solutions that make a difference. 
                  Whether you need AI integration, full-stack development, or creative digital experiences, 
                  I'm here to bring your vision to life.
                </p>

                {/* Contact Methods */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <button
                    className="generate-button"
                    style={{
                      padding: '1rem 1.5rem',
                      background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                      border: 'none',
                      borderRadius: '12px',
                      color: '#ffffff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => {
                      window.open('mailto:ryanllamido@gmail.com?subject=Work Inquiry&body=Hi Ryan,%0D%0A%0D%0AI saw your portfolio and would like to discuss a project opportunity.%0D%0A%0D%0AProject details:%0D%0A- %0D%0A- %0D%0A- %0D%0A%0D%0ABest regards', '_blank');
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 20px rgba(255, 107, 53, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    📧 Email Me
                  </button>

                  <button
                    className="nav-button"
                    style={{
                      padding: '1rem 1.5rem',
                      background: 'linear-gradient(135deg, #333, #444)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      color: '#ffffff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText('ryanllamedo@gmail.com');
                      alert('📋 Email copied to clipboard!\n\ryanllamedo@gmail.com');
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #444, #555)';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #333, #444)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    📋 Copy Email
                  </button>

                  <button
                    className="nav-button"
                    style={{
                      padding: '1rem 1.5rem',
                      background: 'linear-gradient(135deg, #0077b5, #005885)',
                      border: 'none',
                      borderRadius: '12px',
                      color: '#ffffff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => {
                      window.open('https://linkedin.com/in/ryan-developer', '_blank');
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #0088cc, #006699)';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #0077b5, #005885)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    💼 LinkedIn
                  </button>

                  <button
                    className="nav-button"
                    style={{
                      padding: '1rem 1.5rem',
                      background: 'linear-gradient(135deg, #333, #24292e)',
                      border: 'none',
                      borderRadius: '12px',
                      color: '#ffffff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => {
                      window.open('https://github.com/ryan-developer', '_blank');
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #444, #333)';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #333, #24292e)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    🔗 GitHub
                  </button>
                </div>

                {/* Availability Status */}
                <div style={{
                  background: 'rgba(76, 175, 80, 0.1)',
                  border: '1px solid rgba(76, 175, 80, 0.3)',
                  borderRadius: '12px',
                  padding: '1rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    background: '#4CAF50',
                    borderRadius: '50%',
                    animation: 'pulse 2s infinite'
                  }} />
                  <span style={{
                    color: '#4CAF50',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}>
                    Available for new projects
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Find GF Tab */}
          {tab === 'findgf' && (
            <div className="content-section">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  animation: 'pulse 2s infinite'
                }}>
                  💕
                </div>
                <h2 style={{
                  margin: 0,
                  fontSize: '2rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  AI Girlfriend Chat
                </h2>
              </div>

              {!girlfriend ? (
                /* Girlfriend Generation Section */
                <div style={{
                  background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  padding: '2rem',
                  marginBottom: '2rem'
                }}>
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '2rem'
                  }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                      borderRadius: '50%',
                      margin: '0 auto 1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2.5rem',
                      animation: 'pulse 2s infinite'
                    }}>
                      🤖
                    </div>
                    <h3 style={{
                      margin: '0 0 1rem 0',
                      fontSize: '1.8rem',
                      color: '#ffffff'
                    }}>
                      Create Your AI Girlfriend
                    </h3>
                    <p style={{
                      margin: '0 0 1.5rem 0',
                      fontSize: '1.1rem',
                      color: '#ccc',
                      lineHeight: '1.6',
                      maxWidth: '600px',
                      margin: '0 auto 1.5rem auto'
                    }}>
                      Generate a unique AI girlfriend with her own personality, interests, and appearance. 
                      She'll remember your conversations and develop a relationship with you over time.
                    </p>
                  </div>

                  {/* Preferences Input */}
                  <div style={{ marginBottom: '2rem' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      color: '#ccc',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}>
                      Preferences (Optional)
                    </label>
                    <textarea
                      value={preferences}
                      onChange={(e) => setPreferences(e.target.value)}
                      placeholder="Describe what kind of personality, interests, or appearance you'd like... (e.g., 'loves books and coffee, has a bubbly personality, enjoys outdoor activities')"
                      style={{
                        width: '100%',
                        minHeight: '100px',
                        padding: '1rem',
                        background: '#333',
                        border: '1px solid #555',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>

                  {/* Generate Button */}
                  <div style={{ textAlign: 'center' }}>
                    <button
                      onClick={generateGirlfriend}
                      disabled={girlfriendLoading}
                      className="generate-button"
                      style={{
                        padding: '1rem 2rem',
                        background: girlfriendLoading ? '#444' : 'linear-gradient(135deg, #ff6b35, #f7931e)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: girlfriendLoading ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      {girlfriendLoading ? (
                        <>
                          <div className="spinner"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          ✨ Generate AI Girlfriend
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                /* Chat Interface */
                <div className="girlfriend-chat-container">
                  {/* Girlfriend Profile Sidebar */}
                  <div className="girlfriend-profile-sidebar" style={{
                    background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    padding: '1.5rem',
                    height: 'fit-content'
                  }}>
                    {/* Profile Image */}
                    <div style={{
                      textAlign: 'center',
                      marginBottom: '1.5rem'
                    }}>
                      {girlfriend.imageUrl ? (
                        <img
                          src={girlfriend.imageUrl}
                          alt={girlfriend.name}
                          style={{
                            width: '120px',
                            height: '120px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '3px solid #ff6b35',
                            marginBottom: '1rem'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '120px',
                          height: '120px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '3rem',
                          margin: '0 auto 1rem'
                        }}>
                          👩
                        </div>
                      )}
                      <h3 style={{
                        margin: '0 0 0.5rem 0',
                        color: '#ff6b35',
                        fontSize: '1.4rem'
                      }}>
                        {girlfriend.name}
                      </h3>
                      <p style={{
                        margin: 0,
                        color: '#ccc',
                        fontSize: '0.9rem'
                      }}>
                        {girlfriend.age} years old
                      </p>
                    </div>

                    {/* Profile Details */}
                    <div style={{ fontSize: '0.85rem', lineHeight: '1.5' }}>
                      <div style={{ marginBottom: '1rem' }}>
                        <strong style={{ color: '#ff6b35' }}>Personality:</strong>
                        <p style={{ margin: '0.25rem 0 0 0', color: '#ccc' }}>
                          {girlfriend.personality || 'Sweet and caring'}
                        </p>
                      </div>

                      <div style={{ marginBottom: '1rem' }}>
                        <strong style={{ color: '#ff6b35' }}>Interests:</strong>
                        <p style={{ margin: '0.25rem 0 0 0', color: '#ccc' }}>
                          {girlfriend.hobbies || 'Getting to know you better'}
                        </p>
                      </div>

                      <div style={{ marginBottom: '1rem' }}>
                        <strong style={{ color: '#ff6b35' }}>Background:</strong>
                        <p style={{ margin: '0.25rem 0 0 0', color: '#ccc' }}>
                          {girlfriend.background || 'A mysterious and charming personality waiting to be discovered...'}
                        </p>
                      </div>

                      {girlfriend.favorites && (
                        <div style={{ marginBottom: '1rem' }}>
                          <strong style={{ color: '#ff6b35' }}>Favorites:</strong>
                          <p style={{ margin: '0.25rem 0 0 0', color: '#ccc' }}>
                            {renderFavorites(girlfriend.favorites)}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* New Girlfriend Button */}
                    <button
                      onClick={() => {
                        setGirlfriend(null);
                        setChatMessages([]);
                        setPreferences('');
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(255, 107, 53, 0.1)',
                        border: '1px solid rgba(255, 107, 53, 0.3)',
                        borderRadius: '8px',
                        color: '#ff6b35',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        marginTop: '1rem'
                      }}
                    >
                      🔄 Generate New Girlfriend
                    </button>
                  </div>

                  {/* Chat Area */}
                  <div className="girlfriend-chat-area" style={{
                    background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '600px'
                  }}>
                    {/* Chat Header */}
                    <div style={{
                      padding: '1rem 1.5rem',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      background: 'rgba(255, 107, 53, 0.1)'
                    }}>
                      <h4 style={{
                        margin: 0,
                        color: '#ff6b35',
                        fontSize: '1.1rem'
                      }}>
                        💬 Chat with {girlfriend.name}
                      </h4>
                    </div>

                    {/* Messages Area */}
                    <div style={{
                      flex: 1,
                      padding: '1rem',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '1rem'
                    }}>
                      {chatMessages.map((message, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start'
                          }}
                        >
                          <div style={{
                            maxWidth: '70%',
                            padding: '0.75rem 1rem',
                            borderRadius: '12px',
                            background: message.sender === 'user' 
                              ? 'linear-gradient(135deg, #ff6b35, #f7931e)'
                              : message.sender === 'system'
                              ? '#444'
                              : '#333',
                            color: '#ffffff',
                            fontSize: '0.9rem',
                            lineHeight: '1.4'
                          }}>
                            <div>{message.message}</div>
                            <div style={{
                              fontSize: '0.7rem',
                              opacity: 0.7,
                              marginTop: '0.25rem',
                              textAlign: message.sender === 'user' ? 'right' : 'left'
                            }}>
                              {message.timestamp}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {chatLoading && (
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-start'
                        }}>
                          <div style={{
                            padding: '0.75rem 1rem',
                            borderRadius: '12px',
                            background: '#333',
                            color: '#ccc',
                            fontSize: '0.9rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <div className="spinner"></div>
                            {girlfriend.name} is typing...
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Chat Input */}
                    <div style={{
                      padding: '1rem',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      gap: '0.75rem'
                    }}>
                      <textarea
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={handleChatKeyPress}
                        placeholder={`Message ${girlfriend.name}...`}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          background: '#333',
                          border: '1px solid #555',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '0.9rem',
                          resize: 'none',
                          minHeight: '40px',
                          maxHeight: '100px',
                          fontFamily: 'inherit'
                        }}
                        rows={1}
                      />
                      <button
                        onClick={sendChatMessage}
                        disabled={!chatInput.trim() || chatLoading}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: (!chatInput.trim() || chatLoading) ? '#444' : 'linear-gradient(135deg, #ff6b35, #f7931e)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '0.9rem',
                          cursor: (!chatInput.trim() || chatLoading) ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Send 💕
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Sidebar - AdSense */}
        <div className="sidebar-ad" style={{
          width: '220px',
          flexShrink: 0
        }}>
          <div className="sidebar-card" style={{
            background: '#2a2a2a',
            borderRadius: '12px',
            border: '1px solid #333',
            overflow: 'hidden',
            height: 'fit-content',
            minHeight: '600px'
          }}>
            <div style={{
              padding: '1rem',
              borderBottom: '1px solid #333',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ fontSize: '0.8rem', color: '#ff6b35', fontWeight: '600' }}>📢 ADVERTISEMENT</span>
            </div>
            <div style={{
              padding: '1rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '500px'
            }}>
              <AdSense
                adSlot="0987654321"
                adFormat="vertical"
                style={{
                  width: '180px',
                  height: '600px'
                }}
                uniqueKey="right-sidebar"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Floating Player */}
      {floatingPlayer.isVisible && (
        <div className="floating-player" style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: floatingPlayer.isMinimized ? '300px' : '400px',
          height: floatingPlayer.isMinimized ? '60px' : '300px',
          background: 'rgba(0, 0, 0, 0.9)',
          borderRadius: '1rem',
          overflow: 'hidden',
          zIndex: 1000,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
        }}>
          {/* Player Header */}
          <div style={{
            padding: '0.75rem',
            background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.2), rgba(247, 147, 30, 0.2))',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h4 style={{
              margin: 0,
              fontSize: '0.9rem',
              color: '#ffffff',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {floatingPlayer.title}
            </h4>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={toggleFloatingPlayerMinimize}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.2)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              >
                {floatingPlayer.isMinimized ? '⬆️' : '⬇️'}
              </button>
              <button
                onClick={closeFloatingPlayer}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.2)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              >
                ❌
              </button>
            </div>
          </div>

          {!floatingPlayer.isMinimized && (
            <>
              {/* YouTube Player */}
              {floatingPlayer.videoId && (
                <div style={{ height: '150px' }}>
                  <YouTube
                    videoId={floatingPlayer.videoId}
                    opts={{
                      height: '150',
                      width: '100%',
                      playerVars: {
                        autoplay: 1,
                        controls: 1,
                        modestbranding: 1,
                        rel: 0
                      }
                    }}
                    onReady={onPlayerReady}
                    onError={onPlayerError}
                    onStateChange={onPlayerStateChange}
                  />
                </div>
              )}

              {/* Controls */}
              <div style={{
                padding: '0.75rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <button
                  onClick={togglePlayPause}
                  style={{
                    background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.3), rgba(247, 147, 30, 0.3))',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, rgba(255, 107, 53, 0.8), rgba(247, 147, 30, 0.8))';
                    e.target.style.transform = 'scale(1.1)';
                    e.target.style.boxShadow = '0 6px 16px rgba(255, 107, 53, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, rgba(255, 107, 53, 0.3), rgba(247, 147, 30, 0.3))';
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.3)';
                  }}
                >
                  {floatingPlayer.isPlaying ? '⏸️' : '▶️'}
                </button>
              </div>

              {/* Current Lyric */}
              {floatingPlayer.timedLyrics.length > 0 && (
                <div style={{
                  padding: '0.75rem',
                  textAlign: 'center',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: '0.9rem',
                    color: '#ffffff',
                    fontWeight: '500',
                    animation: 'fadeInUp 0.3s ease-out'
                  }}>
                    {floatingPlayer.timedLyrics[getFloatingCurrentIndex()]?.text || 'Loading lyrics...'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Video Modal */}
      {videoModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '2rem'
        }}
        onClick={closeVideoModal}
        >
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '800px',
            overflow: 'hidden',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '1rem 1.5rem',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{
                margin: 0,
                color: '#ffffff',
                fontSize: '1.1rem',
                fontWeight: '600'
              }}>
                {videoModal.title}
              </h3>
              <button
                onClick={closeVideoModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  transition: 'background 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
                onMouseLeave={(e) => e.target.style.background = 'none'}
              >
                ✕
              </button>
            </div>
            
            {/* Video Content */}
            <div style={{
              padding: '1rem',
              height: '500px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <iframe
                src={videoModal.videoUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '8px'
                }}
                title={videoModal.title}
                allowFullScreen
              />
            </div>
            
            {/* Modal Footer */}
            <div style={{
              padding: '1rem 1.5rem',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem'
            }}>
              <button
                onClick={closeVideoModal}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                Close
              </button>
              <a
                href={videoModal.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '0.5rem 1rem',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                🔗 Open in New Tab
              </a>
            </div>
          </div>
        </div>
      )}

      {/* AdSense Banner */}
      <div className="adsense-banner" style={{
        padding: '2rem 0',
        display: 'flex',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 50%, #1a1a1a 100%)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          width: '100%',
          padding: '0 1.5rem'
        }}>
          <div style={{
            background: 'linear-gradient(145deg, #2a2a2a, #1f1f1f)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '1rem',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              marginBottom: '1rem',
              fontSize: '0.8rem',
              color: '#ff6b35',
              fontWeight: '600'
            }}>
              📢 ADVERTISEMENT
            </div>
            <AdSense
              adSlot="2468135790"
              adFormat="horizontal"
              style={{
                width: '100%',
                height: '250px'
              }}
              uniqueKey="bottom-banner"
            />
          </div>
        </div>
      </div>

      {/* Floating Feedback Button */}
      <button
        onClick={openFeedbackModal}
        style={{
          position: 'fixed',
          bottom: '120px',
          right: '20px',
          width: '60px',
          height: '60px',
          background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
          border: 'none',
          borderRadius: '50%',
          color: '#ffffff',
          fontSize: '1.5rem',
          cursor: 'pointer',
          zIndex: 1000,
          boxShadow: '0 8px 24px rgba(255, 107, 53, 0.4)',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
          e.target.style.boxShadow = '0 12px 32px rgba(255, 107, 53, 0.6)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.boxShadow = '0 8px 24px rgba(255, 107, 53, 0.4)';
        }}
        title="Send Feedback"
      >
        💬
      </button>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(5px)'
        }}
        onClick={(e) => e.target === e.currentTarget && closeFeedbackModal()}
        >
          <div style={{
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            borderRadius: '1rem',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '700',
                background: 'linear-gradient(to right, #ff6b35, #f7931e)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                💬 Send Feedback
              </h2>
              <button
                onClick={closeFeedbackModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.2)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              >
                ❌
              </button>
            </div>

            {feedbackSuccess ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem'
              }}>
                <div style={{
                  fontSize: '3rem',
                  marginBottom: '1rem'
                }}>
                  ✅
                </div>
                <h3 style={{
                  color: '#4CAF50',
                  marginBottom: '1rem'
                }}>
                  Thank you for your feedback!
                </h3>
                <p style={{
                  color: '#cccccc',
                  lineHeight: '1.6'
                }}>
                  We've received your message and will review it soon. Your input helps us improve the platform!
                </p>
              </div>
            ) : (
              <div>
                {/* Feedback Type */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '0.5rem'
                  }}>
                    Feedback Type:
                  </label>
                  <select
                    value={feedbackForm.type}
                    onChange={(e) => setFeedbackForm({...feedbackForm, type: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '0.5rem',
                      color: '#ffffff',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  >
                    <option value="general" style={{background: '#1a1a1a'}}>General Feedback</option>
                    <option value="bug" style={{background: '#1a1a1a'}}>Bug Report</option>
                    <option value="feature_request" style={{background: '#1a1a1a'}}>Feature Request</option>
                    <option value="issue" style={{background: '#1a1a1a'}}>Issue/Problem</option>
                  </select>
                </div>

                {/* Priority */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '0.5rem'
                  }}>
                    Priority:
                  </label>
                  <select
                    value={feedbackForm.priority}
                    onChange={(e) => setFeedbackForm({...feedbackForm, priority: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '0.5rem',
                      color: '#ffffff',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  >
                    <option value="low" style={{background: '#1a1a1a'}}>Low</option>
                    <option value="medium" style={{background: '#1a1a1a'}}>Medium</option>
                    <option value="high" style={{background: '#1a1a1a'}}>High</option>
                    <option value="critical" style={{background: '#1a1a1a'}}>Critical</option>
                  </select>
                </div>

                {/* Subject */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '0.5rem'
                  }}>
                    Subject:
                  </label>
                  <input
                    type="text"
                    value={feedbackForm.subject}
                    onChange={(e) => setFeedbackForm({...feedbackForm, subject: e.target.value})}
                    placeholder="Brief description of your feedback..."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '0.5rem',
                      color: '#ffffff',
                      fontSize: '1rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Message */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '0.5rem'
                  }}>
                    Message:
                  </label>
                  <textarea
                    value={feedbackForm.message}
                    onChange={(e) => setFeedbackForm({...feedbackForm, message: e.target.value})}
                    placeholder="Please provide detailed feedback, suggestions, or describe any issues you're experiencing..."
                    rows={6}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '0.5rem',
                      color: '#ffffff',
                      fontSize: '1rem',
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <button
                    onClick={submitFeedback}
                    disabled={feedbackLoading || !feedbackForm.subject.trim() || !feedbackForm.message.trim()}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      background: feedbackLoading || !feedbackForm.subject.trim() || !feedbackForm.message.trim()
                        ? 'rgba(255, 255, 255, 0.2)' 
                        : 'linear-gradient(to right, #ff6b35, #f7931e)',
                      border: 'none',
                      borderRadius: '0.5rem',
                      color: '#ffffff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: feedbackLoading || !feedbackForm.subject.trim() || !feedbackForm.message.trim() ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    {feedbackLoading && (
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid #ffffff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    )}
                    <span>{feedbackLoading ? 'Sending...' : '📤 Send Feedback'}</span>
                  </button>
                  
                  <button
                    onClick={toggleMyFeedback}
                    style={{
                      padding: '1rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '0.5rem',
                      color: '#ffffff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    }}
                  >
                    📋 My Feedback
                  </button>
                </div>

                {/* My Feedback History */}
                {showMyFeedback && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    marginTop: '1rem',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <h4 style={{
                      margin: '0 0 1rem 0',
                      color: '#ff6b35',
                      fontSize: '1rem'
                    }}>
                      📋 Your Feedback History
                    </h4>
                    
                    {userFeedback.length === 0 ? (
                      <p style={{
                        color: '#888',
                        fontStyle: 'italic',
                        margin: 0
                      }}>
                        No feedback submitted yet.
                      </p>
                    ) : (
                      <div style={{
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        {userFeedback.map((feedback, index) => (
                          <div key={feedback._id || index} style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            marginBottom: '1rem',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: '0.5rem'
                            }}>
                              <h5 style={{
                                margin: 0,
                                color: '#ffffff',
                                fontSize: '0.9rem'
                              }}>
                                {feedback.subject}
                              </h5>
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                background: feedback.status === 'open' ? 'rgba(255, 193, 7, 0.2)' : 
                                          feedback.status === 'resolved' ? 'rgba(40, 167, 69, 0.2)' : 
                                          'rgba(108, 117, 125, 0.2)',
                                color: feedback.status === 'open' ? '#ffc107' : 
                                       feedback.status === 'resolved' ? '#28a745' : '#6c757d',
                                borderRadius: '0.25rem',
                                fontSize: '0.7rem',
                                fontWeight: '600'
                              }}>
                                {feedback.status.toUpperCase()}
                              </span>
                            </div>
                            <p style={{
                              margin: '0 0 0.5rem 0',
                              color: '#cccccc',
                              fontSize: '0.8rem',
                              lineHeight: '1.4'
                            }}>
                              {feedback.message.length > 100 ? 
                                feedback.message.substring(0, 100) + '...' : 
                                feedback.message}
                            </p>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              fontSize: '0.7rem',
                              color: '#888'
                            }}>
                              <span>Type: {feedback.type}</span>
                              <span>Priority: {feedback.priority}</span>
                              <span>{new Date(feedback.createdAt).toLocaleDateString()}</span>
                            </div>
                            {feedback.adminResponse && (
                              <div style={{
                                marginTop: '0.5rem',
                                padding: '0.5rem',
                                background: 'rgba(40, 167, 69, 0.1)',
                                borderRadius: '0.25rem',
                                border: '1px solid rgba(40, 167, 69, 0.2)'
                              }}>
                                <div style={{
                                  fontSize: '0.7rem',
                                  color: '#28a745',
                                  fontWeight: '600',
                                  marginBottom: '0.25rem'
                                }}>
                                  Admin Response:
                                </div>
                                <p style={{
                                  margin: 0,
                                  fontSize: '0.8rem',
                                  color: '#cccccc'
                                }}>
                                  {feedback.adminResponse.message}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Tips */}
                <div style={{
                  background: 'rgba(255, 107, 53, 0.05)',
                  border: '1px solid rgba(255, 107, 53, 0.1)',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  marginTop: '1rem'
                }}>
                  <h5 style={{
                    margin: '0 0 0.5rem 0',
                    color: '#ff6b35',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}>
                    💡 Tips for better feedback:
                  </h5>
                  <ul style={{
                    margin: 0,
                    paddingLeft: '1.2rem',
                    fontSize: '0.8rem',
                    color: '#ccc',
                    lineHeight: '1.6'
                  }}>
                    <li>Be specific about what you experienced</li>
                    <li>Include steps to reproduce any issues</li>
                    <li>Mention which feature you were using</li>
                    <li>Suggest improvements or new features</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer style={{
        position: 'fixed',
        bottom: '20px',
        left: '20px',
        right: '20px',
        background: 'linear-gradient(135deg, rgba(15, 15, 15, 0.95), rgba(26, 26, 26, 0.95))',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '1.5rem 2rem',
        zIndex: 999,
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 107, 53, 0.1)',
        animation: 'floatUp 0.6s ease-out',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateY(-4px)';
        e.target.style.boxShadow = '0 25px 50px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 107, 53, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 107, 53, 0.1)';
      }}
      >
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          {/* Creator Section - Compact */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: '#ffffff',
              boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4)',
              transition: 'transform 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.1) rotate(5deg)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1) rotate(0deg)'}
            >
              R
            </div>
            <div>
              <div style={{
                fontSize: '0.9rem',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '0.1rem'
              }}>
                Created by Ryan
              </div>
              <div style={{
                fontSize: '0.7rem',
                color: '#888'
              }}>
                © 2024 LivingSneaky Studio. Built with ❤️ by Ryan.
              </div>
            </div>
          </div>

          {/* Technology Stack - Compact */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center'
          }}>
            <span style={{
              fontSize: '0.7rem',
              color: '#666',
              marginRight: '0.5rem'
            }}>
              Powered by
            </span>
            <span style={{
              padding: '0.25rem 0.6rem',
              background: 'linear-gradient(135deg, rgba(97, 218, 251, 0.2), rgba(97, 218, 251, 0.1))',
              borderRadius: '8px',
              fontSize: '0.65rem',
              color: '#61dafb',
              border: '1px solid rgba(97, 218, 251, 0.3)',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              React
            </span>
            <span style={{
              padding: '0.25rem 0.6rem',
              background: 'linear-gradient(135deg, rgba(104, 201, 176, 0.2), rgba(104, 201, 176, 0.1))',
              borderRadius: '8px',
              fontSize: '0.65rem',
              color: '#68c9b0',
              border: '1px solid rgba(104, 201, 176, 0.3)',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              Node.js
            </span>
            <span style={{
              padding: '0.25rem 0.6rem',
              background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.2), rgba(247, 147, 30, 0.1))',
              borderRadius: '8px',
              fontSize: '0.65rem',
              color: '#ff6b35',
              border: '1px solid rgba(255, 107, 53, 0.3)',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              OpenAI
            </span>
          </div>
        </div>
      </footer>

      {/* Email Collection Modal */}
      <EmailModal
        showEmailModal={showEmailModal}
        emailInput={emailInput}
        setEmailInput={setEmailInput}
        registrationLoading={registrationLoading}
        authError={authError}
        registerUser={registerUser}
      />
    </div>
  );
}

export default App;